"use client";
import { useState, useEffect, useCallback } from "react";

export const useBodyHeight = () => {
  const [bodyHeight, setBodyHeight] = useState<number | null>(null);
  const updateHeight = useCallback(() => {
    if (typeof document === "undefined") return;
    const rafId = requestAnimationFrame(() => {
      const height = Math.max(document.body.clientHeight, window.innerHeight);
      setBodyHeight((prev) => (prev !== height ? height : prev));
    });
    return () => cancelAnimationFrame(rafId);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return () => {};
    // Initial height calculation
    if (
      document.readyState === "interactive" ||
      document.readyState === "complete"
    ) {
      updateHeight();
    } else {
      document.addEventListener("DOMContentLoaded", updateHeight, {
        once: true,
      });
    }

    // Throttle resize events
    let resizeTimeout: NodeJS.Timeout | null = null;
    const handleResize = () => {
      if (resizeTimeout) return;
      resizeTimeout = setTimeout(() => {
        updateHeight();
        resizeTimeout = null;
      }, 100); // 100ms throttle
    };

    // Use ResizeObserver for precise body height changes
    const observer = new ResizeObserver(() => {
      if (resizeTimeout) return; // Reuse throttle to avoid duplicate updates
      resizeTimeout = setTimeout(() => {
        updateHeight();
        resizeTimeout = null;
      }, 100);
    });
    observer.observe(document.body);

    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      document.removeEventListener("DOMContentLoaded", updateHeight);
      window.removeEventListener("resize", handleResize);
      if (resizeTimeout) clearTimeout(resizeTimeout);
      observer.unobserve(document.body);
    };
  }, [updateHeight]);

  return bodyHeight as number;
};

// export const useBodyHeight = () => {
//   const [bodyHeight, setBodyHeight] = useState(0);

//   useEffect(() => {
//     if (typeof document === "undefined") return;

//     const updateHeight = () => {
//       setBodyHeight(document.documentElement.scrollHeight); // better for full height
//     };

//     updateHeight(); // immediately on mount

//     window.addEventListener("resize", updateHeight);
//     window.addEventListener("load", updateHeight);

//     const observer = new MutationObserver(() => {
//       updateHeight();
//     });

//     observer.observe(document.body, {
//       attributes: true,
//       childList: true,
//       subtree: true,
//     });

//     return () => {
//       window.removeEventListener("resize", updateHeight);
//       window.removeEventListener("load", updateHeight);
//       observer.disconnect();
//     };
//   }, []);

//   return bodyHeight;
// };

export default useBodyHeight;

export const SwitchIcon = () => {
  return (
    <svg
      width="60"
      height="60"
      viewBox="0 0 60 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="30" cy="30" r="30" fill="currentColor" />
      <path
        d="M20.0079 33.632C20.0419 33.309 20.2759 33.049 20.7439 32.528L21.7749 31.375C22.0269 31.055 22.2059 30.5 22.2059 30C22.2059 29.5 22.0269 28.944 21.7759 28.625L20.7429 27.472C20.2749 26.952 20.0409 26.691 20.0069 26.368C19.9729 26.045 20.1469 25.741 20.4969 25.134L20.9909 24.277C21.3639 23.629 21.5509 23.305 21.8689 23.177C22.1859 23.047 22.5449 23.149 23.2639 23.353L24.4839 23.696C24.9429 23.802 25.4239 23.742 25.8419 23.527L26.1789 23.333C26.5381 23.1027 26.8142 22.7635 26.9669 22.365L27.3009 21.368C27.5209 20.708 27.6309 20.378 27.8919 20.188C28.1549 20 28.5019 20 29.1959 20H30.3109C31.0049 20 31.3529 20 31.6139 20.189C31.8749 20.378 31.9849 20.708 32.2039 21.368L32.5389 22.365C32.6915 22.7635 32.9677 23.1027 33.3269 23.333L33.6639 23.527C34.0819 23.742 34.5639 23.802 35.0219 23.697L36.2419 23.353C36.9609 23.149 37.3199 23.047 37.6369 23.176C37.9549 23.306 38.1419 23.629 38.5149 24.277L39.0079 25.134C39.3579 25.741 39.5329 26.044 39.4989 26.368C39.4649 26.692 39.2309 26.951 38.7629 27.472L37.7319 28.625C37.4799 28.944 37.3009 29.5 37.3009 30C37.3009 30.5 37.4799 31.056 37.7309 31.375L38.7629 32.528C39.2309 33.048 39.4649 33.309 39.4989 33.632C39.5329 33.955 39.3589 34.259 39.0089 34.866L38.5149 35.723C38.1419 36.371 37.9549 36.695 37.6369 36.823C37.3199 36.953 36.9609 36.851 36.2419 36.647L35.0219 36.304C34.5631 36.1987 34.0819 36.2585 33.6629 36.473L33.3269 36.667C32.9669 36.897 32.6909 37.237 32.5389 37.635L32.2049 38.632C31.9849 39.292 31.8749 39.622 31.6139 39.812C31.3529 40 31.0049 40 30.3109 40H29.1959C28.5019 40 28.1549 40 27.8929 39.811C27.6319 39.622 27.5219 39.293 27.3029 38.633"
        fill="currentColor"
      />
      <path
        d="M20.0079 33.632C20.0419 33.309 20.2759 33.049 20.7439 32.528L21.7749 31.375C22.0269 31.055 22.2059 30.5 22.2059 30C22.2059 29.5 22.0269 28.944 21.7759 28.625L20.7429 27.472C20.2749 26.952 20.0409 26.691 20.0069 26.368C19.9729 26.045 20.1469 25.741 20.4969 25.134L20.9909 24.277C21.3639 23.629 21.5509 23.305 21.8689 23.177C22.1859 23.047 22.5449 23.149 23.2639 23.353L24.4839 23.696C24.9429 23.802 25.4239 23.742 25.8419 23.527L26.1789 23.333C26.5381 23.1027 26.8142 22.7635 26.9669 22.365L27.3009 21.368C27.5209 20.708 27.6309 20.378 27.8919 20.188C28.1549 20 28.5019 20 29.1959 20H30.3109C31.0049 20 31.3529 20 31.6139 20.189C31.8749 20.378 31.9849 20.708 32.2039 21.368L32.5389 22.365C32.6915 22.7635 32.9677 23.1027 33.3269 23.333L33.6639 23.527C34.0819 23.742 34.5639 23.802 35.0219 23.697L36.2419 23.353C36.9609 23.149 37.3199 23.047 37.6369 23.176C37.9549 23.306 38.1419 23.629 38.5149 24.277L39.0079 25.134C39.3579 25.741 39.5329 26.044 39.4989 26.368C39.4649 26.692 39.2309 26.951 38.7629 27.472L37.7319 28.625C37.4799 28.944 37.3009 29.5 37.3009 30C37.3009 30.5 37.4799 31.056 37.7309 31.375L38.7629 32.528C39.2309 33.048 39.4649 33.309 39.4989 33.632C39.5329 33.955 39.3589 34.259 39.0089 34.866L38.5149 35.723C38.1419 36.371 37.9549 36.695 37.6369 36.823C37.3199 36.953 36.9609 36.851 36.2419 36.647L35.0219 36.304C34.5631 36.1987 34.0819 36.2585 33.6629 36.473L33.3269 36.667C32.9669 36.897 32.6909 37.237 32.5389 37.635L32.2049 38.632C31.9849 39.292 31.8749 39.622 31.6139 39.812C31.3529 40 31.0049 40 30.3109 40H29.1959C28.5019 40 28.1549 40 27.8929 39.811C27.6319 39.622 27.5219 39.293 27.3029 38.633"
        stroke="white"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M20.4889 36.78C21.5689 35.7 25.2409 32.064 25.6009 31.644C25.9819 31.2 25.6729 30.6 25.8569 28.74C25.9459 27.84 26.1389 27.166 26.6929 26.664C27.3529 26.04 27.8929 26.04 29.7529 25.998C31.3729 26.04 31.5649 25.86 31.7329 26.28C31.8529 26.58 31.4929 26.76 31.0609 27.24C30.1009 28.2 29.5369 28.68 29.4829 28.98C29.0929 30.3 30.6289 31.08 31.4689 30.24C31.7869 29.922 33.2569 28.44 33.4009 28.32C33.5089 28.224 33.7679 28.228 33.8929 28.38C34.0009 28.486 34.0129 28.5 34.0009 28.98C33.9909 29.424 33.9949 30.062 33.9969 30.72C33.9979 31.572 33.9529 32.52 33.5929 33C32.8729 34.08 31.6729 34.14 30.5929 34.188C29.5729 34.248 28.7329 34.14 28.4689 34.332C28.2529 34.44 27.1129 35.64 25.7329 37.02L23.2729 39.48C21.2329 41.1 18.9889 38.58 20.4889 36.78Z"
        fill="currentColor"
        stroke="white"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};

export const MessageIcon = () => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="20" height="20" rx="4" fill="var(--primary-color)" />
      <path
        d="M5.33341 5.33334H14.6667C15.3084 5.33334 15.8334 5.85834 15.8334 6.50001V13.5C15.8334 14.1417 15.3084 14.6667 14.6667 14.6667H5.33341C4.69175 14.6667 4.16675 14.1417 4.16675 13.5V6.50001C4.16675 5.85834 4.69175 5.33334 5.33341 5.33334Z"
        stroke="white"
        stroke-width="1.04347"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M15.8334 6.5L10.0001 10.5833L4.16675 6.5"
        stroke="white"
        stroke-width="1.04347"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};

export const Logo = () => {
  return (
    <svg
      width="50"
      height="51"
      viewBox="0 0 50 51"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
    >
      <path
        d="M0 0.212891L50 0.212891V50.2129H0L0 0.212891Z"
        fill="url(#pattern0_820_178975)"
      />
      <defs>
        <pattern
          id="pattern0_820_178975"
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use
            xlinkHref="#image0_820_178975"
            transform="translate(0 -0.00859788) scale(0.00132275)"
          />
        </pattern>
        <image
          id="image0_820_178975"
          width="756"
          height="769"
          xlinkHref="data:image/png;base64,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"
        />
      </defs>
    </svg>
  );
};

export const CallIcon = () => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_631_44712)">
        <path
          d="M11.875 3.75C12.9262 4.02681 13.8851 4.57767 14.6537 5.34631C15.4223 6.11494 15.9732 7.07382 16.25 8.125"
          fill="var(--primary-color)"
        />
        <path
          d="M11.875 3.75C12.9262 4.02681 13.8851 4.57767 14.6537 5.34631C15.4223 6.11494 15.9732 7.07382 16.25 8.125"
          stroke="var(--primary-color)"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M11.25 6.25C12.5406 6.59531 13.4047 7.45937 13.75 8.75L11.25 6.25Z"
          fill="var(--primary-color)"
        />
        <path
          d="M11.25 6.25C12.5406 6.59531 13.4047 7.45937 13.75 8.75"
          stroke="var(--primary-color)"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M12.218 11.9797C12.3045 11.9221 12.4041 11.887 12.5076 11.8776C12.6112 11.8682 12.7154 11.8848 12.8109 11.9258L16.4953 13.5766C16.6195 13.6296 16.7231 13.7215 16.7907 13.8384C16.8583 13.9553 16.8862 14.0909 16.8703 14.225C16.7489 15.1321 16.3023 15.9642 15.6133 16.5666C14.9244 17.169 14.0402 17.5007 13.125 17.5C10.3071 17.5 7.60457 16.3806 5.61199 14.388C3.61942 12.3954 2.5 9.69294 2.5 6.87501C2.4993 5.95986 2.831 5.0756 3.43341 4.38668C4.03582 3.69776 4.86793 3.25108 5.775 3.1297C5.90909 3.11377 6.04473 3.1417 6.16162 3.2093C6.27851 3.2769 6.37037 3.38054 6.42344 3.5047L8.07422 7.1922C8.11478 7.28691 8.1313 7.39018 8.1223 7.49282C8.1133 7.59545 8.07907 7.69427 8.02266 7.78048L6.35313 9.76564C6.2939 9.855 6.25888 9.9582 6.25149 10.0651C6.24409 10.1721 6.26458 10.2791 6.31094 10.3758C6.95703 11.6985 8.32422 13.0492 9.65078 13.6891C9.74795 13.7352 9.85548 13.7553 9.96276 13.7472C10.07 13.7391 10.1733 13.7032 10.2625 13.643L12.218 11.9797Z"
          fill="var(--primary-color)"
          stroke="white"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_631_44712">
          <rect width="20" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const LocationIcon = () => {
  return (
    <svg
      width="16"
      height="20"
      viewBox="0 0 16 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_631_44719)">
        <path
          d="M14.25 8.125C14.25 13.75 8 18.125 8 18.125C8 18.125 1.75 13.75 1.75 8.125C1.75 6.4674 2.40848 4.87769 3.58058 3.70558C4.75269 2.53348 6.3424 1.875 8 1.875C9.6576 1.875 11.2473 2.53348 12.4194 3.70558C13.5915 4.87769 14.25 6.4674 14.25 8.125Z"
          fill="var(--primary-color)"
          stroke="white"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M8 10.625C9.38071 10.625 10.5 9.50571 10.5 8.125C10.5 6.74429 9.38071 5.625 8 5.625C6.61929 5.625 5.5 6.74429 5.5 8.125C5.5 9.50571 6.61929 10.625 8 10.625Z"
          fill="var(--primary-color)"
          stroke="white"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_631_44719">
          <rect width="16" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const FacebookIcon = () => {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 28 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M20.9974 2.33594H17.4974C15.9503 2.33594 14.4666 2.95052 13.3726 4.04448C12.2786 5.13844 11.6641 6.62217 11.6641 8.16927V11.6693H8.16406V16.3359H11.6641V25.6693H16.3307V16.3359H19.8307L20.9974 11.6693H16.3307V8.16927C16.3307 7.85985 16.4536 7.56311 16.6724 7.34431C16.8912 7.12552 17.188 7.0026 17.4974 7.0026H20.9974V2.33594Z"
        fill="var(--primary-color)"
      />
    </svg>
  );
};

export const LinkedinIcon = () => {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="16" cy="16" r="16" fill="transparent" />
      <path
        d="M20 12C21.5913 12 23.1174 12.6321 24.2426 13.7574C25.3679 14.8826 26 16.4087 26 18V25H22V18C22 17.4696 21.7893 16.9609 21.4142 16.5858C21.0391 16.2107 20.5304 16 20 16C19.4696 16 18.9609 16.2107 18.5858 16.5858C18.2107 16.9609 18 17.4696 18 18V25H14V18C14 16.4087 14.6321 14.8826 15.7574 13.7574C16.8826 12.6321 18.4087 12 20 12Z"
        fill="var(--primary-color)"
      />
      <path d="M10 13H6V25H10V13Z" fill="var(--primary-color)" />
      <path
        d="M8 10C9.10457 10 10 9.10457 10 8C10 6.89543 9.10457 6 8 6C6.89543 6 6 6.89543 6 8C6 9.10457 6.89543 10 8 10Z"
        fill="var(--primary-color)"
      />
    </svg>
  );
};

export const InstagramIcon = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17 2H7C4.23858 2 2 4.23858 2 7V17C2 19.7614 4.23858 22 7 22H17C19.7614 22 22 19.7614 22 17V7C22 4.23858 19.7614 2 17 2Z"
        fill="var(--primary-color)"
      />
      <path
        d="M15.9997 11.3703C16.1231 12.2025 15.981 13.0525 15.5935 13.7993C15.206 14.5461 14.5929 15.1517 13.8413 15.53C13.0898 15.9082 12.2382 16.0399 11.4075 15.9062C10.5768 15.7726 9.80947 15.3804 9.21455 14.7855C8.61962 14.1905 8.22744 13.4232 8.09377 12.5925C7.96011 11.7619 8.09177 10.9102 8.47003 10.1587C8.84829 9.40716 9.45389 8.79404 10.2007 8.40654C10.9475 8.01904 11.7975 7.87689 12.6297 8.0003C13.4786 8.12619 14.2646 8.52176 14.8714 9.12861C15.4782 9.73545 15.8738 10.5214 15.9997 11.3703Z"
        stroke="white"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M17.5 6.5H17.51"
        stroke="white"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};

export const TwitterIcon = () => {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="16" cy="16" r="16" fill="transparent" />
      <path
        d="M23.5 7H8.5C8.10218 7 7.72064 7.15804 7.43934 7.43934C7.15804 7.72064 7 8.10218 7 8.5V23.5C7 23.8978 7.15804 24.2794 7.43934 24.5607C7.72064 24.842 8.10218 25 8.5 25H23.5C23.8978 25 24.2794 24.842 24.5607 24.5607C24.842 24.2794 25 23.8978 25 23.5V8.5C25 8.10218 24.842 7.72064 24.5607 7.43934C24.2794 7.15804 23.8978 7 23.5 7ZM21.0306 19.9694C21.1003 20.0391 21.1556 20.1218 21.1933 20.2128C21.231 20.3039 21.2504 20.4015 21.2504 20.5C21.2504 20.5985 21.231 20.6961 21.1933 20.7872C21.1556 20.8782 21.1003 20.9609 21.0306 21.0306C20.9609 21.1003 20.8782 21.1556 20.7872 21.1933C20.6961 21.231 20.5985 21.2504 20.5 21.2504C20.4015 21.2504 20.3039 21.231 20.2128 21.1933C20.1218 21.1556 20.0391 21.1003 19.9694 21.0306L16 17.0603L12.0306 21.0306C11.8899 21.1714 11.699 21.2504 11.5 21.2504C11.301 21.2504 11.1101 21.1714 10.9694 21.0306C10.8286 20.8899 10.7496 20.699 10.7496 20.5C10.7496 20.301 10.8286 20.1101 10.9694 19.9694L14.9397 16L10.9694 12.0306C10.8286 11.8899 10.7496 11.699 10.7496 11.5C10.7496 11.301 10.8286 11.1101 10.9694 10.9694C11.1101 10.8286 11.301 10.7496 11.5 10.7496C11.699 10.7496 11.8899 10.8286 12.0306 10.9694L16 14.9397L19.9694 10.9694C20.1101 10.8286 20.301 10.7496 20.5 10.7496C20.699 10.7496 20.8899 10.8286 21.0306 10.9694C21.1714 11.1101 21.2504 11.301 21.2504 11.5C21.2504 11.699 21.1714 11.8899 21.0306 12.0306L17.0603 16L21.0306 19.9694Z"
        fill="var(--primary-color)"
      />
    </svg>
  );
};

export const YoutubeIcon = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className="white"
        d="M22.5406 6.42C22.4218 5.94541 22.1799 5.51057 21.8392 5.15941C21.4986 4.80824 21.0713 4.55318 20.6006 4.42C18.8806 4 12.0006 4 12.0006 4C12.0006 4 5.12057 4 3.40057 4.46C2.92982 4.59318 2.50255 4.84824 2.16192 5.19941C1.82129 5.55057 1.57936 5.98541 1.46057 6.46C1.14579 8.20556 0.991808 9.97631 1.00057 11.75C0.989351 13.537 1.14334 15.3213 1.46057 17.08C1.59153 17.5398 1.83888 17.9581 2.17872 18.2945C2.51855 18.6308 2.93939 18.8738 3.40057 19C5.12057 19.46 12.0006 19.46 12.0006 19.46C12.0006 19.46 18.8806 19.46 20.6006 19C21.0713 18.8668 21.4986 18.6118 21.8392 18.2606C22.1799 17.9094 22.4218 17.4746 22.5406 17C22.8529 15.2676 23.0069 13.5103 23.0006 11.75C23.0118 9.96295 22.8578 8.1787 22.5406 6.42Z"
        fill="var(--primary-color)"
      />
      <path d="M9.75 15.0205L15.5 11.7505L9.75 8.48047V15.0205Z" fill="white" />
    </svg>
  );
};

export const TikTokIcon = () => {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18.8222 9.13333C18.0629 8.26614 17.6443 7.15267 17.6444 6H14.2111V19.7778C14.1852 20.5235 13.8706 21.2301 13.3337 21.7483C12.7968 22.2666 12.0796 22.556 11.3333 22.5556C9.75556 22.5556 8.44445 21.2667 8.44445 19.6667C8.44445 17.7556 10.2889 16.3222 12.1889 16.9111V13.4C8.35556 12.8889 5 15.8667 5 19.6667C5 23.3667 8.06667 26 11.3222 26C14.8111 26 17.6444 23.1667 17.6444 19.6667V12.6778C19.0367 13.6776 20.7082 14.2141 22.4222 14.2111V10.7778C22.4222 10.7778 20.3333 10.8778 18.8222 9.13333Z"
        fill="var(--primary-color)"
      />
    </svg>
  );
};

export const SearchIcon = () => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.58366 17.4998C13.9559 17.4998 17.5003 13.9554 17.5003 9.58317C17.5003 5.21092 13.9559 1.6665 9.58366 1.6665C5.21141 1.6665 1.66699 5.21092 1.66699 9.58317C1.66699 13.9554 5.21141 17.4998 9.58366 17.4998Z"
        stroke="#A4A7B0"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M18.3337 18.3332L16.667 16.6665"
        stroke="#A4A7B0"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};

export const WeirdDots: React.FC<{ className: string }> = ({ className }) => {
  return (
    <span className={`w-[92px] block xs:w-[60px] ${className}`}>
      <svg
        width="100%"
        height="auto"
        viewBox="0 0 96 92"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          opacity="0.85"
          d="M92.7303 90.7912C92.9088 90.7912 93.0534 90.6466 93.0534 90.4681C93.0534 90.2897 92.9088 90.145 92.7303 90.145C92.5519 90.145 92.4072 90.2897 92.4072 90.4681C92.4072 90.6466 92.5519 90.7912 92.7303 90.7912Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.92"
          d="M88.8534 92.4065C89.0318 92.4065 89.1765 92.2618 89.1765 92.0834C89.1765 91.9049 89.0318 91.7603 88.8534 91.7603C88.6749 91.7603 88.5303 91.9049 88.5303 92.0834C88.5303 92.2618 88.6749 92.4065 88.8534 92.4065Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.23"
          d="M95.3153 86.591C95.4937 86.591 95.6384 86.4464 95.6384 86.2679C95.6384 86.0895 95.4937 85.9448 95.3153 85.9448C95.1368 85.9448 94.9922 86.0895 94.9922 86.2679C94.9922 86.4464 95.1368 86.591 95.3153 86.591Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.84"
          d="M90.4676 87.238C90.6461 87.238 90.7907 87.0933 90.7907 86.9149C90.7907 86.7365 90.6461 86.5918 90.4676 86.5918C90.2892 86.5918 90.1445 86.7365 90.1445 86.9149C90.1445 87.0933 90.2892 87.238 90.4676 87.238Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.66"
          d="M85.6214 89.9844C85.889 89.9844 86.106 89.7675 86.106 89.4998C86.106 89.2321 85.889 89.0151 85.6214 89.0151C85.3537 89.0151 85.1367 89.2321 85.1367 89.4998C85.1367 89.7675 85.3537 89.9844 85.6214 89.9844Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.66"
          d="M94.345 82.876C94.6127 82.876 94.8297 82.6591 94.8297 82.3914C94.8297 82.1237 94.6127 81.9067 94.345 81.9067C94.0773 81.9067 93.8604 82.1237 93.8604 82.3914C93.8604 82.6591 94.0773 82.876 94.345 82.876Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.43"
          d="M88.5296 83.8453C88.7972 83.8453 89.0142 83.6283 89.0142 83.3606C89.0142 83.093 88.7972 82.876 88.5296 82.876C88.2619 82.876 88.0449 83.093 88.0449 83.3606C88.0449 83.6283 88.2619 83.8453 88.5296 83.8453Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.92"
          d="M82.7132 87.0753C82.9808 87.0753 83.1978 86.8583 83.1978 86.5906C83.1978 86.3229 82.9808 86.106 82.7132 86.106C82.4455 86.106 82.2285 86.3229 82.2285 86.5906C82.2285 86.8583 82.4455 87.0753 82.7132 87.0753Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.6"
          d="M93.6995 78.6758C93.9672 78.6758 94.1841 78.4589 94.1841 78.1912C94.1841 77.9235 93.9672 77.7065 93.6995 77.7065C93.4318 77.7065 93.2148 77.9235 93.2148 78.1912C93.2148 78.4589 93.4318 78.6758 93.6995 78.6758Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.33"
          d="M77.8675 92.2462C78.1351 92.2462 78.3521 92.0292 78.3521 91.7615C78.3521 91.4938 78.1351 91.2769 77.8675 91.2769C77.5998 91.2769 77.3828 91.4938 77.3828 91.7615C77.3828 92.0292 77.5998 92.2462 77.8675 92.2462Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.13"
          d="M86.5901 80.2906C86.8578 80.2906 87.0748 80.0736 87.0748 79.8059C87.0748 79.5383 86.8578 79.3213 86.5901 79.3213C86.3225 79.3213 86.1055 79.5383 86.1055 79.8059C86.1055 80.0736 86.3225 80.2906 86.5901 80.2906Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.42"
          d="M79.8059 83.8453C80.0736 83.8453 80.2906 83.6283 80.2906 83.3606C80.2906 83.093 80.0736 82.876 79.8059 82.876C79.5383 82.876 79.3213 83.093 79.3213 83.3606C79.3213 83.6283 79.5383 83.8453 79.8059 83.8453Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.5"
          d="M93.053 74.4752C93.3207 74.4752 93.5377 74.2582 93.5377 73.9905C93.5377 73.7228 93.3207 73.5059 93.053 73.5059C92.7853 73.5059 92.5684 73.7228 92.5684 73.9905C92.5684 74.2582 92.7853 74.4752 93.053 74.4752Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.73"
          d="M74.3128 89.9844C74.5804 89.9844 74.7974 89.7675 74.7974 89.4998C74.7974 89.2321 74.5804 89.0151 74.3128 89.0151C74.0451 89.0151 73.8281 89.2321 73.8281 89.4998C73.8281 89.7675 74.0451 89.9844 74.3128 89.9844Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.81"
          d="M84.9753 76.5756C85.3322 76.5756 85.6215 76.2863 85.6215 75.9294C85.6215 75.5725 85.3322 75.2832 84.9753 75.2832C84.6184 75.2832 84.3291 75.5725 84.3291 75.9294C84.3291 76.2863 84.6184 76.5756 84.9753 76.5756Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.18"
          d="M77.5437 80.7748C77.9006 80.7748 78.1899 80.4855 78.1899 80.1286C78.1899 79.7717 77.9006 79.4824 77.5437 79.4824C77.1868 79.4824 76.8975 79.7717 76.8975 80.1286C76.8975 80.4855 77.1868 80.7748 77.5437 80.7748Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.26"
          d="M92.7292 70.4355C93.0861 70.4355 93.3754 70.1462 93.3754 69.7893C93.3754 69.4324 93.0861 69.1431 92.7292 69.1431C92.3723 69.1431 92.083 69.4324 92.083 69.7893C92.083 70.1462 92.3723 70.4355 92.7292 70.4355Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.81"
          d="M71.0827 87.5605C71.4396 87.5605 71.7289 87.2712 71.7289 86.9143C71.7289 86.5574 71.4396 86.2681 71.0827 86.2681C70.7258 86.2681 70.4365 86.5574 70.4365 86.9143C70.4365 87.2712 70.7258 87.5605 71.0827 87.5605Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.8"
          d="M83.6833 72.3749C84.0402 72.3749 84.3295 72.0856 84.3295 71.7287C84.3295 71.3718 84.0402 71.0825 83.6833 71.0825C83.3264 71.0825 83.0371 71.3718 83.0371 71.7287C83.0371 72.0856 83.3264 72.3749 83.6833 72.3749Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.4"
          d="M74.9607 77.2216C75.3175 77.2216 75.6069 76.9323 75.6069 76.5754C75.6069 76.2185 75.3175 75.9292 74.9607 75.9292C74.6038 75.9292 74.3145 76.2185 74.3145 76.5754C74.3145 76.9323 74.6038 77.2216 74.9607 77.2216Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.79"
          d="M92.7292 66.2363C93.0861 66.2363 93.3754 65.9469 93.3754 65.5901C93.3754 65.2332 93.0861 64.9438 92.7292 64.9438C92.3723 64.9438 92.083 65.2332 92.083 65.5901C92.083 65.9469 92.3723 66.2363 92.7292 66.2363Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.59"
          d="M67.8513 84.6532C68.2082 84.6532 68.4975 84.3639 68.4975 84.007C68.4975 83.6502 68.2082 83.3608 67.8513 83.3608C67.4944 83.3608 67.2051 83.6502 67.2051 84.007C67.2051 84.3639 67.4944 84.6532 67.8513 84.6532Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.17"
          d="M82.7146 68.497C83.0715 68.497 83.3608 68.2077 83.3608 67.8508C83.3608 67.4939 83.0715 67.2046 82.7146 67.2046C82.3577 67.2046 82.0684 67.4939 82.0684 67.8508C82.0684 68.2077 82.3577 68.497 82.7146 68.497Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.41"
          d="M73.0212 73.6679C73.3781 73.6679 73.6674 73.3786 73.6674 73.0217C73.6674 72.6648 73.3781 72.3755 73.0212 72.3755C72.6643 72.3755 72.375 72.6648 72.375 73.0217C72.375 73.3786 72.6643 73.6679 73.0212 73.6679Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.67"
          d="M93.0534 62.0361C93.4103 62.0361 93.6996 61.7467 93.6996 61.3899C93.6996 61.033 93.4103 60.7437 93.0534 60.7437C92.6965 60.7437 92.4072 61.033 92.4072 61.3899C92.4072 61.7467 92.6965 62.0361 93.0534 62.0361Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.65"
          d="M64.6193 81.906C65.0654 81.906 65.427 81.5444 65.427 81.0983C65.427 80.6522 65.0654 80.2905 64.6193 80.2905C64.1732 80.2905 63.8115 80.6522 63.8115 81.0983C63.8115 81.5444 64.1732 81.906 64.6193 81.906Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.87"
          d="M81.7453 64.4588C82.1914 64.4588 82.553 64.0971 82.553 63.651C82.553 63.2049 82.1914 62.8433 81.7453 62.8433C81.2991 62.8433 80.9375 63.2049 80.9375 63.651C80.9375 64.0971 81.2991 64.4588 81.7453 64.4588Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.42"
          d="M59.1281 92.5691C59.5742 92.5691 59.9358 92.2075 59.9358 91.7614C59.9358 91.3153 59.5742 90.9536 59.1281 90.9536C58.682 90.9536 58.3203 91.3153 58.3203 91.7614C58.3203 92.2075 58.682 92.5691 59.1281 92.5691Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.62"
          d="M71.0831 69.9519C71.5293 69.9519 71.8909 69.5903 71.8909 69.1442C71.8909 68.6981 71.5293 68.3364 71.0831 68.3364C70.637 68.3364 70.2754 68.6981 70.2754 69.1442C70.2754 69.5903 70.637 69.9519 71.0831 69.9519Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.35"
          d="M93.7003 57.9973C94.1464 57.9973 94.5081 57.6357 94.5081 57.1896C94.5081 56.7435 94.1464 56.3818 93.7003 56.3818C93.2542 56.3818 92.8926 56.7435 92.8926 57.1896C92.8926 57.6357 93.2542 57.9973 93.7003 57.9973Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.72"
          d="M61.713 78.6746C62.1591 78.6746 62.5208 78.3129 62.5208 77.8668C62.5208 77.4207 62.1591 77.0591 61.713 77.0591C61.2669 77.0591 60.9053 77.4207 60.9053 77.8668C60.9053 78.3129 61.2669 78.6746 61.713 78.6746Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.5"
          d="M81.0988 59.9358C81.5449 59.9358 81.9065 59.5742 81.9065 59.1281C81.9065 58.682 81.5449 58.3203 81.0988 58.3203C80.6527 58.3203 80.291 58.682 80.291 59.1281C80.291 59.5742 80.6527 59.9358 81.0988 59.9358Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.22"
          d="M55.2511 90.3069C55.6972 90.3069 56.0589 89.9453 56.0589 89.4992C56.0589 89.0531 55.6972 88.6914 55.2511 88.6914C54.805 88.6914 54.4434 89.0531 54.4434 89.4992C54.4434 89.9453 54.805 90.3069 55.2511 90.3069Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.44"
          d="M69.1447 66.075C69.5908 66.075 69.9524 65.7133 69.9524 65.2672C69.9524 64.8211 69.5908 64.4595 69.1447 64.4595C68.6986 64.4595 68.3369 64.8211 68.3369 65.2672C68.3369 65.7133 68.6986 66.075 69.1447 66.075Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.69"
          d="M94.6681 53.7967C95.1142 53.7967 95.4759 53.435 95.4759 52.9889C95.4759 52.5428 95.1142 52.1812 94.6681 52.1812C94.222 52.1812 93.8604 52.5428 93.8604 52.9889C93.8604 53.435 94.222 53.7967 94.6681 53.7967Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.67"
          d="M59.1281 75.4436C59.5742 75.4436 59.9358 75.082 59.9358 74.6359C59.9358 74.1898 59.5742 73.8281 59.1281 73.8281C58.682 73.8281 58.3203 74.1898 58.3203 74.6359C58.3203 75.082 58.682 75.4436 59.1281 75.4436Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.4"
          d="M80.7765 55.7351C81.2226 55.7351 81.5843 55.3735 81.5843 54.9274C81.5843 54.4813 81.2226 54.1196 80.7765 54.1196C80.3304 54.1196 79.9688 54.4813 79.9688 54.9274C79.9688 55.3735 80.3304 55.7351 80.7765 55.7351Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.54"
          d="M51.6968 87.8834C52.2322 87.8834 52.6661 87.4495 52.6661 86.9141C52.6661 86.3788 52.2322 85.9448 51.6968 85.9448C51.1615 85.9448 50.7275 86.3788 50.7275 86.9141C50.7275 87.4495 51.1615 87.8834 51.6968 87.8834Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.56"
          d="M67.8521 62.0363C68.3875 62.0363 68.8214 61.6023 68.8214 61.067C68.8214 60.5316 68.3875 60.0977 67.8521 60.0977C67.3168 60.0977 66.8828 60.5316 66.8828 61.067C66.8828 61.6023 67.3168 62.0363 67.8521 62.0363Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.47"
          d="M95.9615 49.7575C96.4968 49.7575 96.9308 49.3235 96.9308 48.7882C96.9308 48.2528 96.4968 47.8188 95.9615 47.8188C95.4262 47.8188 94.9922 48.2528 94.9922 48.7882C94.9922 49.3235 95.4262 49.7575 95.9615 49.7575Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.83"
          d="M56.5435 71.7286C57.0789 71.7286 57.5128 71.2947 57.5128 70.7593C57.5128 70.224 57.0789 69.79 56.5435 69.79C56.0082 69.79 55.5742 70.224 55.5742 70.7593C55.5742 71.2947 56.0082 71.7286 56.5435 71.7286Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.65"
          d="M80.7759 51.3737C81.3113 51.3737 81.7453 50.9397 81.7453 50.4044C81.7453 49.869 81.3113 49.4351 80.7759 49.4351C80.2406 49.4351 79.8066 49.869 79.8066 50.4044C79.8066 50.9397 80.2406 51.3737 80.7759 51.3737Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.39"
          d="M48.4664 84.9757C49.0017 84.9757 49.4357 84.5417 49.4357 84.0064C49.4357 83.4711 49.0017 83.0371 48.4664 83.0371C47.931 83.0371 47.4971 83.4711 47.4971 84.0064C47.4971 84.5417 47.931 84.9757 48.4664 84.9757Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.14"
          d="M66.5582 57.5123C67.0935 57.5123 67.5275 57.0784 67.5275 56.543C67.5275 56.0077 67.0935 55.5737 66.5582 55.5737C66.0228 55.5737 65.5889 56.0077 65.5889 56.543C65.5889 57.0784 66.0228 57.5123 66.5582 57.5123Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.6"
          d="M54.2818 67.8517C54.8171 67.8517 55.2511 67.4177 55.2511 66.8824C55.2511 66.3471 54.8171 65.9131 54.2818 65.9131C53.7465 65.9131 53.3125 66.3471 53.3125 66.8824C53.3125 67.4177 53.7465 67.8517 54.2818 67.8517Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.37"
          d="M81.0982 46.8493C81.6335 46.8493 82.0675 46.4153 82.0675 45.8799C82.0675 45.3446 81.6335 44.9106 81.0982 44.9106C80.5629 44.9106 80.1289 45.3446 80.1289 45.8799C80.1289 46.4153 80.5629 46.8493 81.0982 46.8493Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.22"
          d="M45.2349 81.7448C45.7703 81.7448 46.2042 81.3108 46.2042 80.7755C46.2042 80.2401 45.7703 79.8062 45.2349 79.8062C44.6996 79.8062 44.2656 80.2401 44.2656 80.7755C44.2656 81.3108 44.6996 81.7448 45.2349 81.7448Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.75"
          d="M65.5904 53.3121C66.1257 53.3121 66.5597 52.8782 66.5597 52.3428C66.5597 51.8075 66.1257 51.3735 65.5904 51.3735C65.0551 51.3735 64.6211 51.8075 64.6211 52.3428C64.6211 52.8782 65.0551 53.3121 65.5904 53.3121Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.98"
          d="M52.0191 63.9747C52.5544 63.9747 52.9884 63.5408 52.9884 63.0054C52.9884 62.4701 52.5544 62.0361 52.0191 62.0361C51.4838 62.0361 51.0498 62.4701 51.0498 63.0054C51.0498 63.5408 51.4838 63.9747 52.0191 63.9747Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.74"
          d="M81.7437 42.3268C82.2791 42.3268 82.713 41.8928 82.713 41.3575C82.713 40.8222 82.2791 40.3882 81.7437 40.3882C81.2084 40.3882 80.7744 40.8222 80.7744 41.3575C80.7744 41.8928 81.2084 42.3268 81.7437 42.3268Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.44"
          d="M42.0029 78.6753C42.6275 78.6753 43.1338 78.169 43.1338 77.5444C43.1338 76.9199 42.6275 76.4136 42.0029 76.4136C41.3784 76.4136 40.8721 76.9199 40.8721 77.5444C40.8721 78.169 41.3784 78.6753 42.0029 78.6753Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.57"
          d="M64.9424 48.9497C65.5669 48.9497 66.0732 48.4434 66.0732 47.8188C66.0732 47.1943 65.5669 46.688 64.9424 46.688C64.3178 46.688 63.8115 47.1943 63.8115 47.8188C63.8115 48.4434 64.3178 48.9497 64.9424 48.9497Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.16"
          d="M50.4033 59.9351C51.0279 59.9351 51.5342 59.4288 51.5342 58.8042C51.5342 58.1796 51.0279 57.6733 50.4033 57.6733C49.7788 57.6733 49.2725 58.1796 49.2725 58.8042C49.2725 59.4288 49.7788 59.9351 50.4033 59.9351Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.87"
          d="M82.3906 37.9648C83.0152 37.9648 83.5215 37.4585 83.5215 36.834C83.5215 36.2094 83.0152 35.7031 82.3906 35.7031C81.7661 35.7031 81.2598 36.2094 81.2598 36.834C81.2598 37.4585 81.7661 37.9648 82.3906 37.9648Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.11"
          d="M39.0947 75.1206C39.7193 75.1206 40.2256 74.6143 40.2256 73.9897C40.2256 73.3652 39.7193 72.8589 39.0947 72.8589C38.4702 72.8589 37.9639 73.3652 37.9639 73.9897C37.9639 74.6143 38.4702 75.1206 39.0947 75.1206Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.97"
          d="M64.6191 44.4263C65.2437 44.4263 65.75 43.92 65.75 43.2954C65.75 42.6709 65.2437 42.1646 64.6191 42.1646C63.9946 42.1646 63.4883 42.6709 63.4883 43.2954C63.4883 43.92 63.9946 44.4263 64.6191 44.4263Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.62"
          d="M32.3105 93.5386C32.9351 93.5386 33.4414 93.0323 33.4414 92.4077C33.4414 91.7832 32.9351 91.2769 32.3105 91.2769C31.686 91.2769 31.1797 91.7832 31.1797 92.4077C31.1797 93.0323 31.686 93.5386 32.3105 93.5386Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.27"
          d="M48.7891 55.7349C49.4136 55.7349 49.9199 55.2286 49.9199 54.604C49.9199 53.9794 49.4136 53.4731 48.7891 53.4731C48.1645 53.4731 47.6582 53.9794 47.6582 54.604C47.6582 55.2286 48.1645 55.7349 48.7891 55.7349Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.19"
          d="M83.6826 33.4409C84.3072 33.4409 84.8135 32.9346 84.8135 32.3101C84.8135 31.6855 84.3072 31.1792 83.6826 31.1792C83.0581 31.1792 82.5518 31.6855 82.5518 32.3101C82.5518 32.9346 83.0581 33.4409 83.6826 33.4409Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.39"
          d="M36.1875 71.2436C36.8121 71.2436 37.3184 70.7373 37.3184 70.1128C37.3184 69.4882 36.8121 68.9819 36.1875 68.9819C35.5629 68.9819 35.0566 69.4882 35.0566 70.1128C35.0566 70.7373 35.5629 71.2436 36.1875 71.2436Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.2"
          d="M64.2969 39.5796C64.9214 39.5796 65.4277 39.0733 65.4277 38.4487C65.4277 37.8242 64.9214 37.3179 64.2969 37.3179C63.6723 37.3179 63.166 37.8242 63.166 38.4487C63.166 39.0733 63.6723 39.5796 64.2969 39.5796Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.5"
          d="M28.433 90.7914C29.1468 90.7914 29.7254 90.2127 29.7254 89.499C29.7254 88.7852 29.1468 88.2065 28.433 88.2065C27.7193 88.2065 27.1406 88.7852 27.1406 89.499C27.1406 90.2127 27.7193 90.7914 28.433 90.7914Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.67"
          d="M47.1733 51.3739C47.887 51.3739 48.4657 50.7952 48.4657 50.0815C48.4657 49.3677 47.887 48.7891 47.1733 48.7891C46.4595 48.7891 45.8809 49.3677 45.8809 50.0815C45.8809 50.7952 46.4595 51.3739 47.1733 51.3739Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.88"
          d="M84.976 29.079C85.6898 29.079 86.2684 28.5003 86.2684 27.7865C86.2684 27.0728 85.6898 26.4941 84.976 26.4941C84.2622 26.4941 83.6836 27.0728 83.6836 27.7865C83.6836 28.5003 84.2622 29.079 84.976 29.079Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.59"
          d="M33.602 67.5287C34.3158 67.5287 34.8944 66.95 34.8944 66.2363C34.8944 65.5225 34.3158 64.9438 33.602 64.9438C32.8882 64.9438 32.3096 65.5225 32.3096 66.2363C32.3096 66.95 32.8882 67.5287 33.602 67.5287Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.15"
          d="M64.6205 34.8944C65.3343 34.8944 65.9129 34.3158 65.9129 33.602C65.9129 32.8882 65.3343 32.3096 64.6205 32.3096C63.9068 32.3096 63.3281 32.8882 63.3281 33.602C63.3281 34.3158 63.9068 34.8944 64.6205 34.8944Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.26"
          d="M24.8783 87.5604C25.5921 87.5604 26.1708 86.9818 26.1708 86.268C26.1708 85.5542 25.5921 84.9756 24.8783 84.9756C24.1646 84.9756 23.5859 85.5542 23.5859 86.268C23.5859 86.9818 24.1646 87.5604 24.8783 87.5604Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.56"
          d="M45.8803 46.5267C46.5941 46.5267 47.1727 45.9481 47.1727 45.2343C47.1727 44.5205 46.5941 43.9419 45.8803 43.9419C45.1665 43.9419 44.5879 44.5205 44.5879 45.2343C44.5879 45.9481 45.1665 46.5267 45.8803 46.5267Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.46"
          d="M86.5922 24.555C87.306 24.555 87.8846 23.9764 87.8846 23.2626C87.8846 22.5488 87.306 21.9702 86.5922 21.9702C85.8784 21.9702 85.2998 22.5488 85.2998 23.2626C85.2998 23.9764 85.8784 24.555 86.5922 24.555Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.52"
          d="M31.3412 63.3285C32.055 63.3285 32.6336 62.7498 32.6336 62.0361C32.6336 61.3223 32.055 60.7437 31.3412 60.7437C30.6275 60.7437 30.0488 61.3223 30.0488 62.0361C30.0488 62.7498 30.6275 63.3285 31.3412 63.3285Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.19"
          d="M64.9428 30.0482C65.6566 30.0482 66.2352 29.4696 66.2352 28.7558C66.2352 28.042 65.6566 27.4634 64.9428 27.4634C64.229 27.4634 63.6504 28.042 63.6504 28.7558C63.6504 29.4696 64.229 30.0482 64.9428 30.0482Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.69"
          d="M21.3237 84.0072C22.0374 84.0072 22.6161 83.4285 22.6161 82.7148C22.6161 82.001 22.0374 81.4224 21.3237 81.4224C20.6099 81.4224 20.0312 82.001 20.0312 82.7148C20.0312 83.4285 20.6099 84.0072 21.3237 84.0072Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.52"
          d="M45.2348 41.68C45.9486 41.68 46.5272 41.1014 46.5272 40.3876C46.5272 39.6738 45.9486 39.0952 45.2348 39.0952C44.521 39.0952 43.9424 39.6738 43.9424 40.3876C43.9424 41.1014 44.521 41.68 45.2348 41.68Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.97"
          d="M88.5307 20.3558C89.2445 20.3558 89.8231 19.7772 89.8231 19.0634C89.8231 18.3496 89.2445 17.771 88.5307 17.771C87.8169 17.771 87.2383 18.3496 87.2383 19.0634C87.2383 19.7772 87.8169 20.3558 88.5307 20.3558Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.35"
          d="M29.0785 58.8041C29.7923 58.8041 30.3709 58.2254 30.3709 57.5116C30.3709 56.7979 29.7923 56.2192 29.0785 56.2192C28.3648 56.2192 27.7861 56.7979 27.7861 57.5116C27.7861 58.2254 28.3648 58.8041 29.0785 58.8041Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.75"
          d="M65.5893 25.202C66.3031 25.202 66.8817 24.6234 66.8817 23.9096C66.8817 23.1958 66.3031 22.6172 65.5893 22.6172C64.8755 22.6172 64.2969 23.1958 64.2969 23.9096C64.2969 24.6234 64.8755 25.202 65.5893 25.202Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.12"
          d="M18.0926 80.6145C18.8956 80.6145 19.5466 79.9635 19.5466 79.1605C19.5466 78.3575 18.8956 77.7065 18.0926 77.7065C17.2896 77.7065 16.6387 78.3575 16.6387 79.1605C16.6387 79.9635 17.2896 80.6145 18.0926 80.6145Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.71"
          d="M44.5887 36.9948C45.3917 36.9948 46.0427 36.3439 46.0427 35.5409C46.0427 34.7379 45.3917 34.0869 44.5887 34.0869C43.7857 34.0869 43.1348 34.7379 43.1348 35.5409C43.1348 36.3439 43.7857 36.9948 44.5887 36.9948Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.3"
          d="M90.7918 15.9939C91.5948 15.9939 92.2458 15.3429 92.2458 14.5399C92.2458 13.7369 91.5948 13.0859 90.7918 13.0859C89.9888 13.0859 89.3379 13.7369 89.3379 14.5399C89.3379 15.3429 89.9888 15.9939 90.7918 15.9939Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.6"
          d="M27.1395 54.4426C27.9425 54.4426 28.5935 53.7916 28.5935 52.9886C28.5935 52.1856 27.9425 51.5347 27.1395 51.5347C26.3365 51.5347 25.6855 52.1856 25.6855 52.9886C25.6855 53.7916 26.3365 54.4426 27.1395 54.4426Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.13"
          d="M66.5594 20.5168C67.3624 20.5168 68.0134 19.8658 68.0134 19.0628C68.0134 18.2598 67.3624 17.6089 66.5594 17.6089C65.7564 17.6089 65.1055 18.2598 65.1055 19.0628C65.1055 19.8658 65.7564 20.5168 66.5594 20.5168Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.61"
          d="M14.8622 76.736C15.6652 76.736 16.3161 76.0851 16.3161 75.2821C16.3161 74.4791 15.6652 73.8281 14.8622 73.8281C14.0592 73.8281 13.4082 74.4791 13.4082 75.2821C13.4082 76.0851 14.0592 76.736 14.8622 76.736Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.73"
          d="M43.9422 32.1486C44.7452 32.1486 45.3962 31.4977 45.3962 30.6947C45.3962 29.8917 44.7452 29.2407 43.9422 29.2407C43.1392 29.2407 42.4883 29.8917 42.4883 30.6947C42.4883 31.4977 43.1392 32.1486 43.9422 32.1486Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.57"
          d="M93.3758 11.4699C94.1788 11.4699 94.8298 10.819 94.8298 10.016C94.8298 9.21297 94.1788 8.56201 93.3758 8.56201C92.5728 8.56201 91.9219 9.21297 91.9219 10.016C91.9219 10.819 92.5728 11.4699 93.3758 11.4699Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.69"
          d="M25.202 49.5959C26.005 49.5959 26.656 48.9449 26.656 48.1419C26.656 47.3389 26.005 46.688 25.202 46.688C24.399 46.688 23.748 47.3389 23.748 48.1419C23.748 48.9449 24.399 49.5959 25.202 49.5959Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.3"
          d="M67.8524 15.6701C68.6554 15.6701 69.3064 15.0192 69.3064 14.2162C69.3064 13.4132 68.6554 12.7622 67.8524 12.7622C67.0494 12.7622 66.3984 13.4132 66.3984 14.2162C66.3984 15.0192 67.0494 15.6701 67.8524 15.6701Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.24"
          d="M11.954 72.5368C12.757 72.5368 13.4079 71.8859 13.4079 71.0829C13.4079 70.2799 12.757 69.6289 11.954 69.6289C11.151 69.6289 10.5 70.2799 10.5 71.0829C10.5 71.8859 11.151 72.5368 11.954 72.5368Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.96"
          d="M43.9422 26.9787C44.7452 26.9787 45.3962 26.3278 45.3962 25.5248C45.3962 24.7218 44.7452 24.0708 43.9422 24.0708C43.1392 24.0708 42.4883 24.7218 42.4883 25.5248C42.4883 26.3278 43.1392 26.9787 43.9422 26.9787Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.58"
          d="M96.285 7.26973C97.088 7.26973 97.739 6.61877 97.739 5.81577C97.739 5.01278 97.088 4.36182 96.285 4.36182C95.482 4.36182 94.8311 5.01278 94.8311 5.81577C94.8311 6.61877 95.482 7.26973 96.285 7.26973Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.57"
          d="M23.9085 44.9107C24.8007 44.9107 25.524 44.1874 25.524 43.2952C25.524 42.403 24.8007 41.6797 23.9085 41.6797C23.0163 41.6797 22.293 42.403 22.293 43.2952C22.293 44.1874 23.0163 44.9107 23.9085 44.9107Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.52"
          d="M69.4671 10.9849C70.3593 10.9849 71.0826 10.2616 71.0826 9.36942C71.0826 8.47719 70.3593 7.75391 69.4671 7.75391C68.5749 7.75391 67.8516 8.47719 67.8516 9.36942C67.8516 10.2616 68.5749 10.9849 69.4671 10.9849Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.36"
          d="M9.04715 68.1749C9.93937 68.1749 10.6627 67.4516 10.6627 66.5594C10.6627 65.6671 9.93937 64.9438 9.04715 64.9438C8.15493 64.9438 7.43164 65.6671 7.43164 66.5594C7.43164 67.4516 8.15493 68.1749 9.04715 68.1749Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.11"
          d="M44.2639 21.9713C45.1562 21.9713 45.8795 21.248 45.8795 20.3557C45.8795 19.4635 45.1562 18.7402 44.2639 18.7402C43.3717 18.7402 42.6484 19.4635 42.6484 20.3557C42.6484 21.248 43.3717 21.9713 44.2639 21.9713Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.53"
          d="M22.6165 39.7422C23.5087 39.7422 24.232 39.019 24.232 38.1267C24.232 37.2345 23.5087 36.5112 22.6165 36.5112C21.7243 36.5112 21.001 37.2345 21.001 38.1267C21.001 39.019 21.7243 39.7422 22.6165 39.7422Z"
          fill="var(--primary-color)"
        />
        <path
          opacity="0.84"
          d="M71.4055 5.81647C72.2978 5.81647 73.0211 5.09318 73.0211 4.20096C73.0211 3.30874 72.2978 2.58545 71.4055 2.58545C70.5133 2.58545 69.79 3.30874 69.79 4.20096C69.79 5.09318 70.5133 5.81647 71.4055 5.81647Z"
          fill="var(--primary-color)"
        />
      </svg>
    </span>
  );
};

export const Bg = () => {
  const height = useBodyHeight();
  return (
    <div
      style={{
        height,
      }}
      className="absolute sm:hidden pointer-events-none z-[-6] block top-0 left-0 mx-auto will-change-transform  w-full"
    >
      <svg
        width="100%"
        className="max-w-[10000px] mx-auto"
        height={height}
        // preserveAspectRatio="xMidYMid slice"
        viewBox={`0 0 100% ${height}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_688_135211)">
          <rect width="100%" height="100%" fill="#FAFCFF" />
          {/* <g filter="url(#filter0_f_688_135211)">
            <rect
              x="38.9994"
              y="804.052"
              width="328"
              height="243.365"
              fill="url(#paint0_radial_688_135211)"
            />
          </g> */}
          <g filter="url(#filter1_f_688_135211)">
            <rect
              x="82.9994"
              y="2162.22"
              width="336"
              height="243.365"
              fill="#FAFCFF"
            />
          </g>
          <g>
            <rect
              x="896"
              y="1685.06"
              width="336"
              height="243.365"
              fill="#FAFCFF"
            />
          </g>
          <g>
            <rect
              x="809"
              y="3206.89"
              width="336"
              height="243.365"
              fill="#FAFCFF"
            />
          </g>
          <g>
            <rect
              width="336.003"
              height="243.363"
              transform="matrix(-0.999917 0.0128575 -0.0113846 -0.999935 1308.75 2551.17)"
              fill="#FAFCFF"
            />
          </g>
          <g filter="url(#filter5_f_688_135211)">
            <path
              d="M253.276 1292.15C153.276 1241.14 275.776 1145.49 275.776 1145.49L552.276 1012.12C783.776 1145.49 552.276 1292.15 552.276 1292.15C275.776 1524.35 253.276 1292.15 253.276 1292.15Z"
              fill="#FAFCFF"
            />
          </g>
          <g filter="url(#filter6_f_688_135211)">
            <path
              d="M881.579 1005.23C855.509 1119.81 741.922 1012.46 741.922 1012.46L561.475 752.924C635.891 484.483 819.171 694.476 819.171 694.476C1090.57 933.382 881.579 1005.23 881.579 1005.23Z"
              fill="#FAFCFF"
            />
          </g>
          <g filter="url(#filter7_f_688_135211)">
            <path
              d="M61.3946 729.088C81.5745 618.675 208.356 708.615 208.356 708.615L415.468 935.269C354.111 1195.26 147.183 1015.52 147.183 1015.52C-154.618 817.186 61.3946 729.088 61.3946 729.088Z"
              fill="rgba(var(--primary-color-rgb), 0.3)"
            />
          </g>
          <g filter="url(#filter8_f_688_135211)">
            <path
              d="M1046.29 1398.43C1017.18 1506.84 898.153 1406.86 898.153 1406.86L710.21 1164.07C792.561 909.952 984.145 1105.96 984.145 1105.96C1268.77 1328.24 1046.29 1398.43 1046.29 1398.43Z"
              fill="rgba(var(--primary-color-rgb), 0.3)"
            />
          </g>
          <g filter="url(#filter9_f_688_135211)">
            <path
              d="M11.0639 2264.42C-89.0791 2213.73 33.1513 2117.7 33.1513 2117.7L309.275 1983.45C541.149 2116.08 310.062 2263.47 310.062 2263.47C34.2168 2496.56 11.0639 2264.42 11.0639 2264.42Z"
              fill="rgba(var(--primary-color-rgb), 0.3)"
            />
          </g>
          <g filter="url(#filter10_f_688_135211)">
            <path
              d="M474.064 2209.42C373.921 2158.73 496.151 2062.7 496.151 2062.7L772.275 1928.45C1004.15 2061.08 773.062 2208.47 773.062 2208.47C497.217 2441.56 474.064 2209.42 474.064 2209.42Z"
              fill="rgba(var(--primary-color-rgb), 0.3)"
            />
          </g>
          <g filter="url(#filter11_f_688_135211)">
            <path
              d="M921.064 2210.42C820.921 2159.73 943.151 2063.7 943.151 2063.7L1219.27 1929.45C1451.15 2062.08 1220.06 2209.47 1220.06 2209.47C944.217 2442.56 921.064 2210.42 921.064 2210.42Z"
              fill="rgba(var(--primary-color-rgb), 0.3)"
            />
          </g>
          <g filter="url(#filter12_f_688_135211)">
            <path
              d="M836.064 3213.42C735.921 3162.73 858.151 3066.7 858.151 3066.7L1134.27 2932.45C1366.15 3065.08 1135.06 3212.47 1135.06 3212.47C859.217 3445.56 836.064 3213.42 836.064 3213.42Z"
              fill="rgba(var(--primary-color-rgb), 0.3)"
            />
          </g>
          <g filter="url(#filter13_f_688_135211)">
            <path
              d="M522.064 2836.42C421.921 2785.73 544.151 2689.7 544.151 2689.7L820.275 2555.45C1052.15 2688.08 821.062 2835.47 821.062 2835.47C545.217 3068.56 522.064 2836.42 522.064 2836.42Z"
              fill="rgba(var(--primary-color-rgb), 0.3)"
            />
          </g>
          <g filter="url(#filter14_f_688_135211)">
            <path
              d="M-32.4135 1105.4L190.503 1189.35L417.373 1586.34L4.05694 1461.67C-373.383 1217.81 -32.4135 1105.4 -32.4135 1105.4Z"
              fill="#FAFCFF"
            />
          </g>
          <g filter="url(#filter15_f_688_135211)">
            <path
              d="M1249.26 2513.62L1025.4 2432.54L794.027 2038.5L1208.73 2157.84C1588.92 2396.83 1249.26 2513.62 1249.26 2513.62Z"
              fill="#FAFCFF"
            />
          </g>
          <g filter="url(#filter16_f_688_135211)">
            <path
              d="M746.793 3317.57C840.776 3254.96 851.456 3416.1 851.456 3416.1L809.916 3735.77C580.188 3872.55 587.13 3586.23 587.13 3586.23C550.039 3213.79 746.793 3317.57 746.793 3317.57Z"
              fill="#FAFCFF"
            />
          </g>
          <g filter="url(#filter17_f_688_135211)">
            <path
              d="M123.064 4109.42C22.9209 4058.73 145.151 3962.7 145.151 3962.7L421.275 3828.45C653.149 3961.08 422.062 4108.47 422.062 4108.47C146.217 4341.56 123.064 4109.42 123.064 4109.42Z"
              fill="rgba(var(--primary-color-rgb), 0.3)"
            />
          </g>
          <g filter="url(#filter18_f_688_135211)">
            <path
              d="M568.064 3873.42C467.921 3822.73 590.151 3726.7 590.151 3726.7L866.275 3592.45C1098.15 3725.08 867.062 3872.47 867.062 3872.47C591.217 4105.56 568.064 3873.42 568.064 3873.42Z"
              fill="rgba(var(--primary-color-rgb), 0.3)"
            />
          </g>
          <g filter="url(#filter19_f_688_135211)">
            <path
              d="M980.061 3661.14C867.89 3657.11 938.591 3518.67 938.591 3518.67L1132.98 3281.01C1399.1 3304.21 1251.11 3534.91 1251.11 3534.91C1098.41 3862.17 980.061 3661.14 980.061 3661.14Z"
              fill="rgba(var(--primary-color-rgb), 0.3)"
            />
          </g>
          <g filter="url(#filter20_f_688_135211)">
            <path
              d="M947.061 6501.13C834.89 6497.1 905.591 6358.66 905.591 6358.66L1099.98 6121.01C1366.1 6144.2 1218.11 6374.9 1218.11 6374.9C1065.41 6702.17 947.061 6501.13 947.061 6501.13Z"
              fill="rgba(var(--primary-color-rgb), 0.3)"
            />
          </g>
          <g filter="url(#filter21_f_688_135211)">
            <path
              d="M163.577 4311.33C210.978 4209.59 310.947 4328.63 310.947 4328.63L454.129 4600.23C329.123 4836.3 174.275 4610.14 174.275 4610.14C-67.6794 4342.04 163.577 4311.33 163.577 4311.33Z"
              fill="rgba(var(--primary-color-rgb), 0.3)"
            />
          </g>
          <g filter="url(#filter22_f_688_135211)">
            <path
              d="M246.577 5094.33C293.978 4992.59 393.947 5111.63 393.947 5111.63L537.129 5383.23C412.123 5619.3 257.275 5393.14 257.275 5393.14C15.3206 5125.04 246.577 5094.33 246.577 5094.33Z"
              fill="rgba(var(--primary-color-rgb), 0.3)"
            />
          </g>
          <g filter="url(#filter23_f_688_135211)">
            <path
              d="M824.578 5524.33C871.979 5422.59 971.948 5541.63 971.948 5541.63L1115.13 5813.23C990.124 6049.3 835.276 5823.14 835.276 5823.14C593.322 5555.04 824.578 5524.33 824.578 5524.33Z"
              fill="rgba(var(--primary-color-rgb), 0.3)"
            />
          </g>
          <g filter="url(#filter24_f_688_135211)">
            <path
              d="M1005.26 3047.74L819.427 3203.21L382.445 3195.07L702.332 2890.26C1097.9 2681.34 1005.26 3047.74 1005.26 3047.74Z"
              fill="#FAFCFF"
            />
          </g>
          <g filter="url(#filter25_f_688_135211)">
            <path
              d="M1287.82 5007.12L1052.03 4987.71L735.757 4667.16L1165.36 4673.21C1589.51 4804.27 1287.82 5007.12 1287.82 5007.12Z"
              fill="#FAFCFF"
            />
          </g>
        </g>
        <defs>
          <filter
            id="filter0_f_688_135211"
            x="-161.001"
            y="604.052"
            width="728"
            height="643.365"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="100"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter1_f_688_135211"
            x="-117.001"
            y="1962.22"
            width="736"
            height="643.365"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="100"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter2_f_688_135211"
            x="696"
            y="1485.06"
            width="736"
            height="643.365"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="100"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter3_f_688_135211"
            x="609"
            y="3006.89"
            width="736"
            height="643.365"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="100"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter4_f_688_135211"
            x="770"
            y="2107.82"
            width="738.746"
            height="647.668"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="100"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter5_f_688_135211"
            x="13.999"
            y="812.118"
            width="841.166"
            height="783.233"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="100"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter6_f_688_135211"
            x="61.4747"
            y="116.321"
            width="1396.93"
            height="1441.78"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="250"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter7_f_688_135211"
            x="-363.476"
            y="324.081"
            width="1128.94"
            height="1101.96"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="175"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter8_f_688_135211"
            x="360.21"
            y="683.812"
            width="1118.83"
            height="1115.09"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="175"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter9_f_688_135211"
            x="-378.36"
            y="1633.45"
            width="1140.9"
            height="1083.87"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="175"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter10_f_688_135211"
            x="84.6396"
            y="1578.45"
            width="1140.9"
            height="1083.87"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="175"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter11_f_688_135211"
            x="531.64"
            y="1579.45"
            width="1140.9"
            height="1083.87"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="175"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter12_f_688_135211"
            x="446.64"
            y="2582.45"
            width="1140.9"
            height="1083.87"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="175"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter13_f_688_135211"
            x="132.64"
            y="2205.45"
            width="1140.9"
            height="1083.87"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="175"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter14_f_688_135211"
            x="-673.701"
            y="605.396"
            width="1591.07"
            height="1480.94"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="250"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter15_f_688_135211"
            x="294.027"
            y="1538.5"
            width="1594.86"
            height="1475.12"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="250"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter16_f_688_135211"
            x="282.475"
            y="3003.12"
            width="868.981"
            height="1069.66"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="150"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter17_f_688_135211"
            x="-266.36"
            y="3478.44"
            width="1140.9"
            height="1083.87"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="175"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter18_f_688_135211"
            x="178.64"
            y="3242.44"
            width="1140.9"
            height="1083.87"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="175"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter19_f_688_135211"
            x="566.606"
            y="2931.01"
            width="1075.54"
            height="1141.22"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="175"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter20_f_688_135211"
            x="533.606"
            y="5771.01"
            width="1075.54"
            height="1141.22"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="175"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter21_f_688_135211"
            x="-286.106"
            y="3920.2"
            width="1090.23"
            height="1137.58"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="175"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter22_f_688_135211"
            x="-203.106"
            y="4703.2"
            width="1090.23"
            height="1137.58"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="175"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter23_f_688_135211"
            x="374.895"
            y="5133.2"
            width="1090.23"
            height="1137.58"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="175"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter24_f_688_135211"
            x="-117.555"
            y="2325.88"
            width="1632.24"
            height="1377.33"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="250"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <filter
            id="filter25_f_688_135211"
            x="235.757"
            y="4167.16"
            width="1656.09"
            height="1339.96"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="250"
              result="effect1_foregroundBlur_688_135211"
            />
          </filter>
          <radialGradient
            id="paint0_radial_688_135211"
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(150.285 892.79) rotate(38.666) scale(198.163 297.227)"
          >
            <stop stop-color="#23282D" />
            <stop offset="1" stop-color="#738193" />
          </radialGradient>
          <clipPath id="clip0_688_135211">
            <rect width="100%" height="100%" fill="white" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
};

export const Bg2 = () => {
  return (
    <span className="absolute top-0 left-0 w-full h-full">
      <svg
        width="100%"
        className="max-w-[1600px] mx-auto"
        height="100%"
        viewBox="0 0 100% 100%"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_631_44610)">
          <rect
            x="806"
            y="327.454"
            width="100"
            height="100"
            rx="8"
            transform="rotate(-32.3124 806 327.454)"
            fill="var(--primary-color)"
          />
          <rect
            opacity="0.3"
            x="651"
            y="70.4531"
            width="60"
            height="60"
            rx="8"
            transform="rotate(-32.3124 651 70.4531)"
            fill="var(--primary-color)"
          />
          <rect
            x="123.396"
            y="56.5352"
            width="100"
            height="100"
            rx="8"
            transform="rotate(-34.8472 123.396 56.5352)"
            fill="var(--primary-color)"
          />
          <rect
            x="-13"
            y="-8.87207"
            width="100"
            height="100"
            rx="8"
            transform="rotate(-5.81277 -13 -8.87207)"
            fill="var(--primary-color)"
          />
          <rect
            x="-117"
            y="139.453"
            width="100"
            height="100"
            rx="8"
            transform="rotate(-32.3124 -117 139.453)"
            fill="var(--primary-color)"
          />
          <rect
            x="54"
            y="119"
            width="100"
            height="100"
            rx="8"
            fill="var(--primary-color)"
          />
          <rect
            x="86"
            y="282.543"
            width="100"
            height="100"
            rx="8"
            transform="rotate(-30.3602 86 282.543)"
            fill="var(--primary-color)"
          />
          <rect
            x="-48.3862"
            y="321.747"
            width="100"
            height="100"
            rx="8"
            transform="rotate(-72.0517 -48.3862 321.747)"
            fill="var(--primary-color)"
          />
          <rect
            x="237.459"
            y="129"
            width="100"
            height="100"
            rx="8"
            transform="rotate(26.3975 237.459 129)"
            fill="var(--primary-color)"
          />
          <rect
            x="276.955"
            y="29.9551"
            width="100"
            height="100"
            rx="8"
            fill="var(--primary-color)"
          />
          <rect
            x="312.77"
            y="-109.452"
            width="100"
            height="100"
            rx="8"
            transform="rotate(79.1773 312.77 -109.452)"
            fill="var(--primary-color)"
          />
          <rect
            width="100"
            height="100"
            rx="8"
            transform="matrix(-0.820679 -0.57139 -0.57139 0.820679 1315.56 335.535)"
            fill="var(--primary-color)"
          />
          <rect
            width="100"
            height="100"
            rx="8"
            transform="matrix(-0.994858 -0.101278 -0.101278 0.994858 1451.96 270.128)"
            fill="var(--primary-color)"
          />
          <rect
            width="100"
            height="100"
            rx="8"
            transform="matrix(-0.845146 -0.534535 -0.534535 0.845146 1555.96 418.453)"
            fill="var(--primary-color)"
          />
          <rect
            width="100"
            height="100"
            rx="8"
            transform="matrix(-0.989107 0.147201 0.147201 0.989107 1342.23 155)"
            fill="var(--primary-color)"
          />
          <rect
            width="100"
            height="100"
            rx="8"
            transform="matrix(-1 0 0 1 1384.96 398)"
            fill="var(--primary-color)"
          />
          <rect
            width="100"
            height="100"
            rx="8"
            transform="matrix(-0.895732 0.444595 0.444595 0.895732 1201.5 408)"
            fill="var(--primary-color)"
          />
          <rect
            width="100"
            height="100"
            rx="8"
            transform="matrix(-1 0 0 1 1162 308.955)"
            fill="var(--primary-color)"
          />
          <rect
            width="100"
            height="100"
            rx="8"
            transform="matrix(-0.18777 0.982213 0.982213 0.18777 1126.19 169.548)"
            fill="var(--primary-color)"
          />
          <circle
            opacity="0.5"
            cx="563"
            cy="283"
            r="50"
            fill="var(--primary-color)"
          />
          <circle
            opacity="0.5"
            cx="377"
            cy="176"
            r="20"
            fill="var(--primary-color)"
          />
          <circle
            opacity="0.5"
            cx="1024"
            cy="30"
            r="50"
            fill="var(--primary-color)"
          />
          <path
            d="M730.424 433.004L715.686 518.344L649.149 462.91L730.424 433.004Z"
            fill="var(--primary-color)"
          />
          <path
            d="M819.401 106.824L831.227 148.479L789.24 137.894L819.401 106.824Z"
            fill="var(--primary-color)"
          />
        </g>
        <defs>
          <clipPath id="clip0_631_44610">
            <rect width="100%" height="6000" fill="white" />
          </clipPath>
        </defs>
      </svg>
    </span>
  );
};

export const Bg3 = () => {
  const height = useBodyHeight();
  return (
    <div
      style={{
        height,
        backgroundColor: "rgba(var(--primary-color-rgb),0.1)",
      }}
      className="absolute sm:hidden pointer-events-none z-[-6] block top-0 left-0 mx-auto  w-full"
    >
      <svg
        width="100%"
        className="max-w-[2000px] mx-auto"
        height={height}
        viewBox="0 0 1440 6732"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_1118_159680)">
          <rect width="1440" height="6732" fill="none" />
          <g opacity="0.1">
            <circle
              cx="157"
              cy="869"
              r="299.5"
              stroke="var(--primary-color)"
              strokeDasharray="32 32"
            />
            <circle
              cx="157"
              cy="869"
              r="289.5"
              stroke="var(--primary-color)"
              strokeDasharray="28 28"
            />
            <circle
              cx="157"
              cy="869"
              r="279.5"
              stroke="var(--primary-color)"
              strokeDasharray="24 24"
            />
            <circle
              cx="157"
              cy="869"
              r="269.5"
              stroke="var(--primary-color)"
              strokeDasharray="20 20"
            />
            <circle
              cx="157"
              cy="869"
              r="9.5"
              stroke="var(--primary-color)"
              strokeDasharray="2 2"
            />
            <circle
              cx="157"
              cy="869"
              r="49.5"
              stroke="var(--primary-color)"
              strokeDasharray="2 2"
            />
            <circle
              cx="157"
              cy="869"
              r="59.5"
              stroke="var(--primary-color)"
              strokeDasharray="3 3"
            />
            <circle
              cx="157"
              cy="869"
              r="69.5"
              stroke="var(--primary-color)"
              strokeDasharray="4 4"
            />
            <circle
              cx="157"
              cy="869"
              r="79.5"
              stroke="var(--primary-color)"
              strokeDasharray="5 5"
            />
            <circle
              cx="157"
              cy="869"
              r="89.5"
              stroke="var(--primary-color)"
              strokeDasharray="6 6"
            />
            <circle
              cx="157"
              cy="869"
              r="99.5"
              stroke="var(--primary-color)"
              strokeDasharray="7 7"
            />
            <circle
              cx="157"
              cy="869"
              r="149.5"
              stroke="var(--primary-color)"
              strokeDasharray="7 7"
            />
            <circle
              cx="157"
              cy="869"
              r="159.5"
              stroke="var(--primary-color)"
              strokeDasharray="8 8"
            />
            <circle
              cx="157"
              cy="869"
              r="169.5"
              stroke="var(--primary-color)"
              strokeDasharray="9 9"
            />
            <circle
              cx="157"
              cy="869"
              r="179.5"
              stroke="var(--primary-color)"
              strokeDasharray="10 10"
            />
            <circle
              cx="157"
              cy="869"
              r="189.5"
              stroke="var(--primary-color)"
              strokeDasharray="11 11"
            />
            <circle
              cx="157"
              cy="869"
              r="199.5"
              stroke="var(--primary-color)"
              strokeDasharray="12 12"
            />
            <circle
              cx="157"
              cy="869"
              r="239.5"
              stroke="var(--primary-color)"
              strokeDasharray="16 16"
            />
            <circle
              cx="157"
              cy="869"
              r="249.5"
              stroke="var(--primary-color)"
              strokeDasharray="17 17"
            />
            <circle
              cx="157"
              cy="869"
              r="259.5"
              stroke="var(--primary-color)"
              strokeDasharray="18 18"
            />
          </g>
          <g opacity="0.1">
            <circle
              cx="1252"
              cy="200"
              r="299.5"
              stroke="var(--primary-color)"
              strokeDasharray="32 32"
            />
            <circle
              cx="1252"
              cy="200"
              r="289.5"
              stroke="var(--primary-color)"
              strokeDasharray="28 28"
            />
            <circle
              cx="1252"
              cy="200"
              r="279.5"
              stroke="var(--primary-color)"
              strokeDasharray="24 24"
            />
            <circle
              cx="1252"
              cy="200"
              r="269.5"
              stroke="var(--primary-color)"
              strokeDasharray="20 20"
            />
            <circle
              cx="1252"
              cy="200"
              r="9.5"
              stroke="var(--primary-color)"
              strokeDasharray="2 2"
            />
            <circle
              cx="1252"
              cy="200"
              r="49.5"
              stroke="var(--primary-color)"
              strokeDasharray="2 2"
            />
            <circle
              cx="1252"
              cy="200"
              r="59.5"
              stroke="var(--primary-color)"
              strokeDasharray="3 3"
            />
            <circle
              cx="1252"
              cy="200"
              r="69.5"
              stroke="var(--primary-color)"
              strokeDasharray="4 4"
            />
            <circle
              cx="1252"
              cy="200"
              r="79.5"
              stroke="var(--primary-color)"
              strokeDasharray="5 5"
            />
            <circle
              cx="1252"
              cy="200"
              r="89.5"
              stroke="var(--primary-color)"
              strokeDasharray="6 6"
            />
            <circle
              cx="1252"
              cy="200"
              r="99.5"
              stroke="var(--primary-color)"
              strokeDasharray="7 7"
            />
            <circle
              cx="1252"
              cy="200"
              r="149.5"
              stroke="var(--primary-color)"
              strokeDasharray="7 7"
            />
            <circle
              cx="1252"
              cy="200"
              r="159.5"
              stroke="var(--primary-color)"
              strokeDasharray="8 8"
            />
            <circle
              cx="1252"
              cy="200"
              r="169.5"
              stroke="var(--primary-color)"
              strokeDasharray="9 9"
            />
            <circle
              cx="1252"
              cy="200"
              r="179.5"
              stroke="var(--primary-color)"
              strokeDasharray="10 10"
            />
            <circle
              cx="1252"
              cy="200"
              r="189.5"
              stroke="var(--primary-color)"
              strokeDasharray="11 11"
            />
            <circle
              cx="1252"
              cy="200"
              r="199.5"
              stroke="var(--primary-color)"
              strokeDasharray="12 12"
            />
            <circle
              cx="1252"
              cy="200"
              r="239.5"
              stroke="var(--primary-color)"
              strokeDasharray="16 16"
            />
            <circle
              cx="1252"
              cy="200"
              r="249.5"
              stroke="var(--primary-color)"
              strokeDasharray="17 17"
            />
            <circle
              cx="1252"
              cy="200"
              r="259.5"
              stroke="var(--primary-color)"
              strokeDasharray="18 18"
            />
          </g>
          <g opacity="0.2">
            <circle
              cx="556"
              cy="1215"
              r="29.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="3.2 3.2"
            />
            <circle
              cx="556"
              cy="1215"
              r="28.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="2.8 2.8"
            />
            <circle
              cx="556"
              cy="1215"
              r="27.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="2.4 2.4"
            />
            <circle
              cx="556"
              cy="1215"
              r="26.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="2 2"
            />
            <circle
              cx="556"
              cy="1215"
              r="0.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="0.2 0.2"
            />
            <circle
              cx="556"
              cy="1215"
              r="4.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="0.2 0.2"
            />
            <circle
              cx="556"
              cy="1215"
              r="5.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="0.3 0.3"
            />
            <circle
              cx="556"
              cy="1215"
              r="6.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="0.4 0.4"
            />
            <circle
              cx="556"
              cy="1215"
              r="7.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="0.5 0.5"
            />
            <circle
              cx="556"
              cy="1215"
              r="8.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="0.6 0.6"
            />
            <circle
              cx="556"
              cy="1215"
              r="9.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="0.7 0.7"
            />
            <circle
              cx="556"
              cy="1215"
              r="14.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="0.7 0.7"
            />
            <circle
              cx="556"
              cy="1215"
              r="15.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="0.8 0.8"
            />
            <circle
              cx="556"
              cy="1215"
              r="16.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="0.9 0.9"
            />
            <circle
              cx="556"
              cy="1215"
              r="17.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="1 1"
            />
            <circle
              cx="556"
              cy="1215"
              r="18.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="1.1 1.1"
            />
            <circle
              cx="556"
              cy="1215"
              r="19.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="1.2 1.2"
            />
            <circle
              cx="556"
              cy="1215"
              r="23.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="1.6 1.6"
            />
            <circle
              cx="556"
              cy="1215"
              r="24.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="1.7 1.7"
            />
            <circle
              cx="556"
              cy="1215"
              r="25.95"
              stroke="var(--primary-color)"
              stroke-width="0.1"
              strokeDasharray="1.8 1.8"
            />
          </g>
          <g opacity="0.1">
            <circle
              cx="1272"
              cy="1795"
              r="399.333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="42.67 42.67"
            />
            <circle
              cx="1272"
              cy="1795"
              r="386"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="37.33 37.33"
            />
            <circle
              cx="1272"
              cy="1795"
              r="372.667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="32 32"
            />
            <circle
              cx="1272"
              cy="1795"
              r="359.333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="26.67 26.67"
            />
            <circle
              cx="1272"
              cy="1795"
              r="12.6667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="2.67 2.67"
            />
            <circle
              cx="1272"
              cy="1795"
              r="66"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="2.67 2.67"
            />
            <circle
              cx="1272"
              cy="1795"
              r="79.3333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="4 4"
            />
            <circle
              cx="1272"
              cy="1795"
              r="92.6667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="5.33 5.33"
            />
            <circle
              cx="1272"
              cy="1795"
              r="106"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="6.67 6.67"
            />
            <circle
              cx="1272"
              cy="1795"
              r="119.333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="8 8"
            />
            <circle
              cx="1272"
              cy="1795"
              r="132.667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="9.33 9.33"
            />
            <circle
              cx="1272"
              cy="1795"
              r="199.333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="9.33 9.33"
            />
            <circle
              cx="1272"
              cy="1795"
              r="212.667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="10.67 10.67"
            />
            <circle
              cx="1272"
              cy="1795"
              r="226"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="12 12"
            />
            <circle
              cx="1272"
              cy="1795"
              r="239.333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="13.33 13.33"
            />
            <circle
              cx="1272"
              cy="1795"
              r="252.667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="14.67 14.67"
            />
            <circle
              cx="1272"
              cy="1795"
              r="266"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="16 16"
            />
            <circle
              cx="1272"
              cy="1795"
              r="319.333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="21.33 21.33"
            />
            <circle
              cx="1272"
              cy="1795"
              r="332.667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="22.67 22.67"
            />
            <circle
              cx="1272"
              cy="1795"
              r="346"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="24 24"
            />
          </g>
          <g opacity="0.15">
            <circle
              cx="1059"
              cy="899"
              r="99.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="10.67 10.67"
            />
            <circle
              cx="1059"
              cy="899"
              r="96.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="9.33 9.33"
            />
            <circle
              cx="1059"
              cy="899"
              r="93.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="8 8"
            />
            <circle
              cx="1059"
              cy="899"
              r="89.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="6.67 6.67"
            />
            <circle
              cx="1059"
              cy="899"
              r="3.16667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="0.67 0.67"
            />
            <circle
              cx="1059"
              cy="899"
              r="16.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="0.67 0.67"
            />
            <circle
              cx="1059"
              cy="899"
              r="19.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="1 1"
            />
            <circle
              cx="1059"
              cy="899"
              r="23.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="1.33 1.33"
            />
            <circle
              cx="1059"
              cy="899"
              r="26.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="1.67 1.67"
            />
            <circle
              cx="1059"
              cy="899"
              r="29.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2 2"
            />
            <circle
              cx="1059"
              cy="899"
              r="33.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2.33 2.33"
            />
            <circle
              cx="1059"
              cy="899"
              r="49.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2.33 2.33"
            />
            <circle
              cx="1059"
              cy="899"
              r="53.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2.67 2.67"
            />
            <circle
              cx="1059"
              cy="899"
              r="56.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="3 3"
            />
            <circle
              cx="1059"
              cy="899"
              r="59.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="3.33 3.33"
            />
            <circle
              cx="1059"
              cy="899"
              r="63.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="3.67 3.67"
            />
            <circle
              cx="1059"
              cy="899"
              r="66.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="4 4"
            />
            <circle
              cx="1059"
              cy="899"
              r="79.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="5.33 5.33"
            />
            <circle
              cx="1059"
              cy="899"
              r="83.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="5.67 5.67"
            />
            <circle
              cx="1059"
              cy="899"
              r="86.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="6 6"
            />
          </g>
          <g opacity="0.15">
            <circle
              cx="457"
              cy="260"
              r="99.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="10.67 10.67"
            />
            <circle
              cx="457.001"
              cy="260"
              r="96.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="9.33 9.33"
            />
            <circle
              cx="456.999"
              cy="260"
              r="93.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="8 8"
            />
            <circle
              cx="457"
              cy="260"
              r="89.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="6.67 6.67"
            />
            <circle
              cx="456.999"
              cy="260"
              r="3.16667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="0.67 0.67"
            />
            <circle
              cx="457.001"
              cy="260"
              r="16.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="0.67 0.67"
            />
            <circle
              cx="457"
              cy="260"
              r="19.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="1 1"
            />
            <circle
              cx="456.999"
              cy="260"
              r="23.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="1.33 1.33"
            />
            <circle
              cx="457.001"
              cy="260"
              r="26.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="1.67 1.67"
            />
            <circle
              cx="457"
              cy="260"
              r="29.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2 2"
            />
            <circle
              cx="456.999"
              cy="260"
              r="33.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2.33 2.33"
            />
            <circle
              cx="457"
              cy="260"
              r="49.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2.33 2.33"
            />
            <circle
              cx="456.999"
              cy="260"
              r="53.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2.67 2.67"
            />
            <circle
              cx="457.001"
              cy="260"
              r="56.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="3 3"
            />
            <circle
              cx="457"
              cy="260"
              r="59.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="3.33 3.33"
            />
            <circle
              cx="456.999"
              cy="260"
              r="63.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="3.67 3.67"
            />
            <circle
              cx="457.001"
              cy="260"
              r="66.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="4 4"
            />
            <circle
              cx="457"
              cy="260"
              r="79.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="5.33 5.33"
            />
            <circle
              cx="456.999"
              cy="260"
              r="83.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="5.67 5.67"
            />
            <circle
              cx="457.001"
              cy="260"
              r="86.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="6 6"
            />
          </g>
          <g opacity="0.15">
            <circle
              cx="417"
              cy="2395"
              r="199.667"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="21.33 21.33"
            />
            <circle
              cx="417.001"
              cy="2395"
              r="193"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="18.67 18.67"
            />
            <circle
              cx="416.999"
              cy="2395"
              r="186.333"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="16 16"
            />
            <circle
              cx="417"
              cy="2395"
              r="179.667"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="13.33 13.33"
            />
            <circle
              cx="416.999"
              cy="2395"
              r="6.33333"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="1.33 1.33"
            />
            <circle
              cx="417.001"
              cy="2395"
              r="33"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="1.33 1.33"
            />
            <circle
              cx="417"
              cy="2395"
              r="39.6667"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="2 2"
            />
            <circle
              cx="416.999"
              cy="2395"
              r="46.3333"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="2.67 2.67"
            />
            <circle
              cx="417.001"
              cy="2395"
              r="53"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="3.33 3.33"
            />
            <circle
              cx="417"
              cy="2395"
              r="59.6667"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="4 4"
            />
            <circle
              cx="416.999"
              cy="2395"
              r="66.3333"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="4.67 4.67"
            />
            <circle
              cx="417"
              cy="2395"
              r="99.6667"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="4.67 4.67"
            />
            <circle
              cx="416.999"
              cy="2395"
              r="106.333"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="5.33 5.33"
            />
            <circle
              cx="417.001"
              cy="2395"
              r="113"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="6 6"
            />
            <circle
              cx="417"
              cy="2395"
              r="119.667"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="6.67 6.67"
            />
            <circle
              cx="416.999"
              cy="2395"
              r="126.333"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="7.33 7.33"
            />
            <circle
              cx="417.001"
              cy="2395"
              r="133"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="8 8"
            />
            <circle
              cx="417"
              cy="2395"
              r="159.667"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="10.67 10.67"
            />
            <circle
              cx="416.999"
              cy="2395"
              r="166.333"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="11.33 11.33"
            />
            <circle
              cx="417.001"
              cy="2395"
              r="173"
              stroke="var(--primary-color)"
              stroke-width="0.666667"
              strokeDasharray="12 12"
            />
          </g>
          <g opacity="0.1">
            <circle
              cx="-64"
              cy="3239"
              r="399.333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="42.67 42.67"
            />
            <circle
              cx="-63.9974"
              cy="3239"
              r="386"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="37.33 37.33"
            />
            <circle
              cx="-64.0026"
              cy="3239"
              r="372.667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="32 32"
            />
            <circle
              cx="-64"
              cy="3239"
              r="359.333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="26.67 26.67"
            />
            <circle
              cx="-63.9974"
              cy="3239"
              r="66"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="2.67 2.67"
            />
            <circle
              cx="-64"
              cy="3239"
              r="79.3333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="4 4"
            />
            <circle
              cx="-64.0026"
              cy="3239"
              r="92.6667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="5.33 5.33"
            />
            <circle
              cx="-63.9974"
              cy="3239"
              r="106"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="6.67 6.67"
            />
            <circle
              cx="-64"
              cy="3239"
              r="119.333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="8 8"
            />
            <circle
              cx="-64.0026"
              cy="3239"
              r="132.667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="9.33 9.33"
            />
            <circle
              cx="-64"
              cy="3239"
              r="199.333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="9.33 9.33"
            />
            <circle
              cx="-64.0026"
              cy="3239"
              r="212.667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="10.67 10.67"
            />
            <circle
              cx="-63.9974"
              cy="3239"
              r="226"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="12 12"
            />
            <circle
              cx="-64"
              cy="3239"
              r="239.333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="13.33 13.33"
            />
            <circle
              cx="-64.0026"
              cy="3239"
              r="252.667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="14.67 14.67"
            />
            <circle
              cx="-63.9974"
              cy="3239"
              r="266"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="16 16"
            />
            <circle
              cx="-64"
              cy="3239"
              r="319.333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="21.33 21.33"
            />
            <circle
              cx="-64.0026"
              cy="3239"
              r="332.667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="22.67 22.67"
            />
            <circle
              cx="-63.9974"
              cy="3239"
              r="346"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="24 24"
            />
          </g>
          <g opacity="0.18">
            <circle
              cx="794"
              cy="2966"
              r="99.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="10.67 10.67"
            />
            <circle
              cx="794.001"
              cy="2966"
              r="96.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="9.33 9.33"
            />
            <circle
              cx="793.999"
              cy="2966"
              r="93.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="8 8"
            />
            <circle
              cx="794"
              cy="2966"
              r="89.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="6.67 6.67"
            />
            <circle
              cx="793.999"
              cy="2966"
              r="3.16667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="0.67 0.67"
            />
            <circle
              cx="794.001"
              cy="2966"
              r="16.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="0.67 0.67"
            />
            <circle
              cx="794"
              cy="2966"
              r="19.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="1 1"
            />
            <circle
              cx="793.999"
              cy="2966"
              r="23.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="1.33 1.33"
            />
            <circle
              cx="794.001"
              cy="2966"
              r="26.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="1.67 1.67"
            />
            <circle
              cx="794"
              cy="2966"
              r="29.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2 2"
            />
            <circle
              cx="793.999"
              cy="2966"
              r="33.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2.33 2.33"
            />
            <circle
              cx="794"
              cy="2966"
              r="49.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2.33 2.33"
            />
            <circle
              cx="793.999"
              cy="2966"
              r="53.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2.67 2.67"
            />
            <circle
              cx="794.001"
              cy="2966"
              r="56.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="3 3"
            />
            <circle
              cx="794"
              cy="2966"
              r="59.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="3.33 3.33"
            />
            <circle
              cx="793.999"
              cy="2966"
              r="63.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="3.67 3.67"
            />
            <circle
              cx="794.001"
              cy="2966"
              r="66.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="4 4"
            />
            <circle
              cx="794"
              cy="2966"
              r="79.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="5.33 5.33"
            />
            <circle
              cx="793.999"
              cy="2966"
              r="83.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="5.67 5.67"
            />
            <circle
              cx="794.001"
              cy="2966"
              r="86.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="6 6"
            />
          </g>
          <g opacity="0.18">
            <circle
              cx="570"
              cy="3720"
              r="99.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="10.67 10.67"
            />
            <circle
              cx="570.001"
              cy="3720"
              r="96.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="9.33 9.33"
            />
            <circle
              cx="569.999"
              cy="3720"
              r="93.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="8 8"
            />
            <circle
              cx="570"
              cy="3720"
              r="89.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="6.67 6.67"
            />
            <circle
              cx="569.999"
              cy="3720"
              r="3.16667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="0.67 0.67"
            />
            <circle
              cx="570.001"
              cy="3720"
              r="16.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="0.67 0.67"
            />
            <circle
              cx="570"
              cy="3720"
              r="19.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="1 1"
            />
            <circle
              cx="569.999"
              cy="3720"
              r="23.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="1.33 1.33"
            />
            <circle
              cx="570.001"
              cy="3720"
              r="26.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="1.67 1.67"
            />
            <circle
              cx="570"
              cy="3720"
              r="29.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2 2"
            />
            <circle
              cx="569.999"
              cy="3720"
              r="33.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2.33 2.33"
            />
            <circle
              cx="570"
              cy="3720"
              r="49.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2.33 2.33"
            />
            <circle
              cx="569.999"
              cy="3720"
              r="53.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2.67 2.67"
            />
            <circle
              cx="570.001"
              cy="3720"
              r="56.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="3 3"
            />
            <circle
              cx="570"
              cy="3720"
              r="59.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="3.33 3.33"
            />
            <circle
              cx="569.999"
              cy="3720"
              r="63.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="3.67 3.67"
            />
            <circle
              cx="570.001"
              cy="3720"
              r="66.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="4 4"
            />
            <circle
              cx="570"
              cy="3720"
              r="79.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="5.33 5.33"
            />
            <circle
              cx="569.999"
              cy="3720"
              r="83.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="5.67 5.67"
            />
            <circle
              cx="570.001"
              cy="3720"
              r="86.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="6 6"
            />
          </g>
          <g opacity="0.18">
            <circle
              cx="447"
              cy="6035"
              r="99.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="10.67 10.67"
            />
            <circle
              cx="447.001"
              cy="6035"
              r="96.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="9.33 9.33"
            />
            <circle
              cx="446.999"
              cy="6035"
              r="93.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="8 8"
            />
            <circle
              cx="447"
              cy="6035"
              r="89.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="6.67 6.67"
            />
            <circle
              cx="446.999"
              cy="6035"
              r="3.16667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="0.67 0.67"
            />
            <circle
              cx="447.001"
              cy="6035"
              r="16.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="0.67 0.67"
            />
            <circle
              cx="447"
              cy="6035"
              r="19.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="1 1"
            />
            <circle
              cx="446.999"
              cy="6035"
              r="23.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="1.33 1.33"
            />
            <circle
              cx="447.001"
              cy="6035"
              r="26.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="1.67 1.67"
            />
            <circle
              cx="447"
              cy="6035"
              r="29.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2 2"
            />
            <circle
              cx="446.999"
              cy="6035"
              r="33.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2.33 2.33"
            />
            <circle
              cx="447"
              cy="6035"
              r="49.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2.33 2.33"
            />
            <circle
              cx="446.999"
              cy="6035"
              r="53.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="2.67 2.67"
            />
            <circle
              cx="447.001"
              cy="6035"
              r="56.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="3 3"
            />
            <circle
              cx="447"
              cy="6035"
              r="59.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="3.33 3.33"
            />
            <circle
              cx="446.999"
              cy="6035"
              r="63.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="3.67 3.67"
            />
            <circle
              cx="447.001"
              cy="6035"
              r="66.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="4 4"
            />
            <circle
              cx="447"
              cy="6035"
              r="79.8333"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="5.33 5.33"
            />
            <circle
              cx="446.999"
              cy="6035"
              r="83.1667"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="5.67 5.67"
            />
            <circle
              cx="447.001"
              cy="6035"
              r="86.5"
              stroke="var(--primary-color)"
              stroke-width="0.333333"
              strokeDasharray="6 6"
            />
          </g>
          <g opacity="0.1">
            <circle
              cx="1225"
              cy="4120"
              r="299.5"
              stroke="var(--primary-color)"
              strokeDasharray="32 32"
            />
            <circle
              cx="1225"
              cy="4120"
              r="289.5"
              stroke="var(--primary-color)"
              strokeDasharray="28 28"
            />
            <circle
              cx="1225"
              cy="4120"
              r="279.5"
              stroke="var(--primary-color)"
              strokeDasharray="24 24"
            />
            <circle
              cx="1225"
              cy="4120"
              r="269.5"
              stroke="var(--primary-color)"
              strokeDasharray="20 20"
            />
            <circle
              cx="1225"
              cy="4120"
              r="9.5"
              stroke="var(--primary-color)"
              strokeDasharray="2 2"
            />
            <circle
              cx="1225"
              cy="4120"
              r="49.5"
              stroke="var(--primary-color)"
              strokeDasharray="2 2"
            />
            <circle
              cx="1225"
              cy="4120"
              r="59.5"
              stroke="var(--primary-color)"
              strokeDasharray="3 3"
            />
            <circle
              cx="1225"
              cy="4120"
              r="69.5"
              stroke="var(--primary-color)"
              strokeDasharray="4 4"
            />
            <circle
              cx="1225"
              cy="4120"
              r="79.5"
              stroke="var(--primary-color)"
              strokeDasharray="5 5"
            />
            <circle
              cx="1225"
              cy="4120"
              r="89.5"
              stroke="var(--primary-color)"
              strokeDasharray="6 6"
            />
            <circle
              cx="1225"
              cy="4120"
              r="99.5"
              stroke="var(--primary-color)"
              strokeDasharray="7 7"
            />
            <circle
              cx="1225"
              cy="4120"
              r="149.5"
              stroke="var(--primary-color)"
              strokeDasharray="7 7"
            />
            <circle
              cx="1225"
              cy="4120"
              r="159.5"
              stroke="var(--primary-color)"
              strokeDasharray="8 8"
            />
            <circle
              cx="1225"
              cy="4120"
              r="169.5"
              stroke="var(--primary-color)"
              strokeDasharray="9 9"
            />
            <circle
              cx="1225"
              cy="4120"
              r="179.5"
              stroke="var(--primary-color)"
              strokeDasharray="10 10"
            />
            <circle
              cx="1225"
              cy="4120"
              r="189.5"
              stroke="var(--primary-color)"
              strokeDasharray="11 11"
            />
            <circle
              cx="1225"
              cy="4120"
              r="199.5"
              stroke="var(--primary-color)"
              strokeDasharray="12 12"
            />
            <circle
              cx="1225"
              cy="4120"
              r="239.5"
              stroke="var(--primary-color)"
              strokeDasharray="16 16"
            />
            <circle
              cx="1225"
              cy="4120"
              r="249.5"
              stroke="var(--primary-color)"
              strokeDasharray="17 17"
            />
            <circle
              cx="1225"
              cy="4120"
              r="259.5"
              stroke="var(--primary-color)"
              strokeDasharray="18 18"
            />
          </g>
          <g opacity="0.1">
            <circle
              cx="1245"
              cy="6135"
              r="299.5"
              stroke="var(--primary-color)"
              strokeDasharray="32 32"
            />
            <circle
              cx="1245"
              cy="6135"
              r="289.5"
              stroke="var(--primary-color)"
              strokeDasharray="28 28"
            />
            <circle
              cx="1245"
              cy="6135"
              r="279.5"
              stroke="var(--primary-color)"
              strokeDasharray="24 24"
            />
            <circle
              cx="1245"
              cy="6135"
              r="269.5"
              stroke="var(--primary-color)"
              strokeDasharray="20 20"
            />
            <circle
              cx="1245"
              cy="6135"
              r="9.5"
              stroke="var(--primary-color)"
              strokeDasharray="2 2"
            />
            <circle
              cx="1245"
              cy="6135"
              r="49.5"
              stroke="var(--primary-color)"
              strokeDasharray="2 2"
            />
            <circle
              cx="1245"
              cy="6135"
              r="59.5"
              stroke="var(--primary-color)"
              strokeDasharray="3 3"
            />
            <circle
              cx="1245"
              cy="6135"
              r="69.5"
              stroke="var(--primary-color)"
              strokeDasharray="4 4"
            />
            <circle
              cx="1245"
              cy="6135"
              r="79.5"
              stroke="var(--primary-color)"
              strokeDasharray="5 5"
            />
            <circle
              cx="1245"
              cy="6135"
              r="89.5"
              stroke="var(--primary-color)"
              strokeDasharray="6 6"
            />
            <circle
              cx="1245"
              cy="6135"
              r="99.5"
              stroke="var(--primary-color)"
              strokeDasharray="7 7"
            />
            <circle
              cx="1245"
              cy="6135"
              r="149.5"
              stroke="var(--primary-color)"
              strokeDasharray="7 7"
            />
            <circle
              cx="1245"
              cy="6135"
              r="159.5"
              stroke="var(--primary-color)"
              strokeDasharray="8 8"
            />
            <circle
              cx="1245"
              cy="6135"
              r="169.5"
              stroke="var(--primary-color)"
              strokeDasharray="9 9"
            />
            <circle
              cx="1245"
              cy="6135"
              r="179.5"
              stroke="var(--primary-color)"
              strokeDasharray="10 10"
            />
            <circle
              cx="1245"
              cy="6135"
              r="189.5"
              stroke="var(--primary-color)"
              strokeDasharray="11 11"
            />
            <circle
              cx="1245"
              cy="6135"
              r="199.5"
              stroke="var(--primary-color)"
              strokeDasharray="12 12"
            />
            <circle
              cx="1245"
              cy="6135"
              r="239.5"
              stroke="var(--primary-color)"
              strokeDasharray="16 16"
            />
            <circle
              cx="1245"
              cy="6135"
              r="249.5"
              stroke="var(--primary-color)"
              strokeDasharray="17 17"
            />
            <circle
              cx="1245"
              cy="6135"
              r="259.5"
              stroke="var(--primary-color)"
              strokeDasharray="18 18"
            />
          </g>
          <g opacity="0.1">
            <circle
              cx="466"
              cy="6882"
              r="299.5"
              stroke="var(--primary-color)"
              strokeDasharray="32 32"
            />
            <circle
              cx="466"
              cy="6882"
              r="289.5"
              stroke="var(--primary-color)"
              strokeDasharray="28 28"
            />
            <circle
              cx="466"
              cy="6882"
              r="279.5"
              stroke="var(--primary-color)"
              strokeDasharray="24 24"
            />
            <circle
              cx="466"
              cy="6882"
              r="269.5"
              stroke="var(--primary-color)"
              strokeDasharray="20 20"
            />
            <circle
              cx="466"
              cy="6882"
              r="159.5"
              stroke="var(--primary-color)"
              strokeDasharray="8 8"
            />
            <circle
              cx="466"
              cy="6882"
              r="169.5"
              stroke="var(--primary-color)"
              strokeDasharray="9 9"
            />
            <circle
              cx="466"
              cy="6882"
              r="179.5"
              stroke="var(--primary-color)"
              strokeDasharray="10 10"
            />
            <circle
              cx="466"
              cy="6882"
              r="189.5"
              stroke="var(--primary-color)"
              strokeDasharray="11 11"
            />
            <circle
              cx="466"
              cy="6882"
              r="199.5"
              stroke="var(--primary-color)"
              strokeDasharray="12 12"
            />
            <circle
              cx="466"
              cy="6882"
              r="239.5"
              stroke="var(--primary-color)"
              strokeDasharray="16 16"
            />
            <circle
              cx="466"
              cy="6882"
              r="249.5"
              stroke="var(--primary-color)"
              strokeDasharray="17 17"
            />
            <circle
              cx="466"
              cy="6882"
              r="259.5"
              stroke="var(--primary-color)"
              strokeDasharray="18 18"
            />
          </g>
          <g opacity="0.2">
            <circle
              cx="1148.5"
              cy="3371.5"
              r="47.4208"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="5.07 5.07"
            />
            <circle
              cx="1148.5"
              cy="3371.5"
              r="45.8375"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="4.43 4.43"
            />
            <circle
              cx="1148.5"
              cy="3371.5"
              r="44.2542"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="3.8 3.8"
            />
            <circle
              cx="1148.5"
              cy="3371.5"
              r="42.6708"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="3.17 3.17"
            />
            <circle
              cx="1148.5"
              cy="3371.5"
              r="1.50417"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="0.32 0.32"
            />
            <circle
              cx="1148.5"
              cy="3371.5"
              r="7.8375"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="0.32 0.32"
            />
            <circle
              cx="1148.5"
              cy="3371.5"
              r="9.42083"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="0.47 0.47"
            />
            <circle
              cx="1148.5"
              cy="3371.5"
              r="11.0042"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="0.63 0.63"
            />
            <circle
              cx="1148.5"
              cy="3371.5"
              r="12.5875"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="0.79 0.79"
            />
            <circle
              cx="1148.5"
              cy="3371.5"
              r="14.1708"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="0.95 0.95"
            />
            <circle
              cx="1148.5"
              cy="3371.5"
              r="15.7542"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="1.11 1.11"
            />
            <circle
              cx="1148.5"
              cy="3371.5"
              r="23.6708"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="1.11 1.11"
            />
            <circle
              cx="1148.5"
              cy="3371.5"
              r="25.2542"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="1.27 1.27"
            />
            <circle
              cx="1148.5"
              cy="3371.5"
              r="26.8375"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="1.42 1.42"
            />
            <circle
              cx="1148.5"
              cy="3371.5"
              r="28.4208"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="1.58 1.58"
            />
            <circle
              cx="1148.5"
              cy="3371.5"
              r="30.0042"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="1.74 1.74"
            />
            <circle
              cx="1148.5"
              cy="3371.5"
              r="31.5875"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="1.9 1.9"
            />
            <circle
              cx="1148.5"
              cy="3371.5"
              r="37.9208"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="2.53 2.53"
            />
            <circle
              cx="1148.5"
              cy="3371.5"
              r="39.5042"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="2.69 2.69"
            />
            <circle
              cx="1148.5"
              cy="3371.5"
              r="41.0875"
              stroke="var(--primary-color)"
              stroke-width="0.158333"
              strokeDasharray="2.85 2.85"
            />
          </g>
          <g opacity="0.1">
            <circle
              cx="299"
              cy="5123"
              r="399.333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="42.67 42.67"
            />
            <circle
              cx="299.003"
              cy="5123"
              r="386"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="37.33 37.33"
            />
            <circle
              cx="298.997"
              cy="5123"
              r="372.667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="32 32"
            />
            <circle
              cx="299"
              cy="5123"
              r="359.333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="26.67 26.67"
            />
            <circle
              cx="298.997"
              cy="5123"
              r="12.6667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="2.67 2.67"
            />
            <circle
              cx="299.003"
              cy="5123"
              r="66"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="2.67 2.67"
            />
            <circle
              cx="299"
              cy="5123"
              r="79.3333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="4 4"
            />
            <circle
              cx="298.997"
              cy="5123"
              r="92.6667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="5.33 5.33"
            />
            <circle
              cx="299.003"
              cy="5123"
              r="106"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="6.67 6.67"
            />
            <circle
              cx="299"
              cy="5123"
              r="119.333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="8 8"
            />
            <circle
              cx="298.997"
              cy="5123"
              r="132.667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="9.33 9.33"
            />
            <circle
              cx="299"
              cy="5123"
              r="199.333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="9.33 9.33"
            />
            <circle
              cx="298.997"
              cy="5123"
              r="212.667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="10.67 10.67"
            />
            <circle
              cx="299.003"
              cy="5123"
              r="226"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="12 12"
            />
            <circle
              cx="299"
              cy="5123"
              r="239.333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="13.33 13.33"
            />
            <circle
              cx="298.997"
              cy="5123"
              r="252.667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="14.67 14.67"
            />
            <circle
              cx="299.003"
              cy="5123"
              r="266"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="16 16"
            />
            <circle
              cx="299"
              cy="5123"
              r="319.333"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="21.33 21.33"
            />
            <circle
              cx="298.997"
              cy="5123"
              r="332.667"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="22.67 22.67"
            />
            <circle
              cx="299.003"
              cy="5123"
              r="346"
              stroke="var(--primary-color)"
              stroke-width="1.33333"
              strokeDasharray="24 24"
            />
          </g>
        </g>
        <defs>
          <clipPath id="clip0_1118_159680">
            <rect width="1440" height="6732" fill="none" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
};

// export const Bg3 = () => {
//   const height = useBodyHeight();
//   return (
//     <div
//       style={{
//         height,
//         backgroundColor: "rgba(var(--primary-color-rgb),0.1)",
//       }}
//       className="absolute pointer-events-none z-[-6] block top-0 left-0 mx-auto  w-full"
//     >
//       <svg
//         width="100%"
//         className="max-w-[2000px] mx-auto"
//         height={height}
//         viewBox={`0 0 100% 100%`}
//         fill="none"
//         xmlns="http://www.w3.org/2000/svg"
//       >
//         <g clipPath="url(#clip0_1118_159680)">
//           <rect width="100%" height={height} fillOpacity="0.01" fill="none" />
//           <g opacity="0.1">
//             <circle
//               cx="157"
//               cy="869"
//               r="299.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="32 32"
//             />
//             <circle
//               cx="157"
//               cy="869"
//               r="289.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="28 28"
//             />
//             <circle
//               cx="157"
//               cy="869"
//               r="279.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="24 24"
//             />
//             <circle
//               cx="157"
//               cy="869"
//               r="269.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="20 20"
//             />
//             <circle
//               cx="157"
//               cy="869"
//               r="9.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="2 2"
//             />
//             <circle
//               cx="157"
//               cy="869"
//               r="49.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="2 2"
//             />
//             <circle
//               cx="157"
//               cy="869"
//               r="59.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="3 3"
//             />
//             <circle
//               cx="157"
//               cy="869"
//               r="69.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="4 4"
//             />
//             <circle
//               cx="157"
//               cy="869"
//               r="79.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="5 5"
//             />
//             <circle
//               cx="157"
//               cy="869"
//               r="89.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="6 6"
//             />
//             <circle
//               cx="157"
//               cy="869"
//               r="99.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="7 7"
//             />
//             <circle
//               cx="157"
//               cy="869"
//               r="149.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="7 7"
//             />
//             <circle
//               cx="157"
//               cy="869"
//               r="159.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="8 8"
//             />
//             <circle
//               cx="157"
//               cy="869"
//               r="169.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="9 9"
//             />
//             <circle
//               cx="157"
//               cy="869"
//               r="179.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="10 10"
//             />
//             <circle
//               cx="157"
//               cy="869"
//               r="189.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="11 11"
//             />
//             <circle
//               cx="157"
//               cy="869"
//               r="199.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="12 12"
//             />
//             <circle
//               cx="157"
//               cy="869"
//               r="239.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="16 16"
//             />
//             <circle
//               cx="157"
//               cy="869"
//               r="249.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="17 17"
//             />
//             <circle
//               cx="157"
//               cy="869"
//               r="259.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="18 18"
//             />
//           </g>
//           <g opacity="0.1">
//             <circle
//               cx="1252"
//               cy="200"
//               r="299.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="32 32"
//             />
//             <circle
//               cx="1252"
//               cy="200"
//               r="289.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="28 28"
//             />
//             <circle
//               cx="1252"
//               cy="200"
//               r="279.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="24 24"
//             />
//             <circle
//               cx="1252"
//               cy="200"
//               r="269.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="20 20"
//             />
//             <circle
//               cx="1252"
//               cy="200"
//               r="9.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="2 2"
//             />
//             <circle
//               cx="1252"
//               cy="200"
//               r="49.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="2 2"
//             />
//             <circle
//               cx="1252"
//               cy="200"
//               r="59.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="3 3"
//             />
//             <circle
//               cx="1252"
//               cy="200"
//               r="69.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="4 4"
//             />
//             <circle
//               cx="1252"
//               cy="200"
//               r="79.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="5 5"
//             />
//             <circle
//               cx="1252"
//               cy="200"
//               r="89.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="6 6"
//             />
//             <circle
//               cx="1252"
//               cy="200"
//               r="99.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="7 7"
//             />
//             <circle
//               cx="1252"
//               cy="200"
//               r="149.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="7 7"
//             />
//             <circle
//               cx="1252"
//               cy="200"
//               r="159.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="8 8"
//             />
//             <circle
//               cx="1252"
//               cy="200"
//               r="169.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="9 9"
//             />
//             <circle
//               cx="1252"
//               cy="200"
//               r="179.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="10 10"
//             />
//             <circle
//               cx="1252"
//               cy="200"
//               r="189.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="11 11"
//             />
//             <circle
//               cx="1252"
//               cy="200"
//               r="199.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="12 12"
//             />
//             <circle
//               cx="1252"
//               cy="200"
//               r="239.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="16 16"
//             />
//             <circle
//               cx="1252"
//               cy="200"
//               r="249.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="17 17"
//             />
//             <circle
//               cx="1252"
//               cy="200"
//               r="259.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="18 18"
//             />
//           </g>
//           <g opacity="0.2">
//             <circle
//               cx="556"
//               cy="1215"
//               r="29.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="3.2 3.2"
//             />
//             <circle
//               cx="556"
//               cy="1215"
//               r="28.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="2.8 2.8"
//             />
//             <circle
//               cx="556"
//               cy="1215"
//               r="27.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="2.4 2.4"
//             />
//             <circle
//               cx="556"
//               cy="1215"
//               r="26.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="2 2"
//             />
//             <circle
//               cx="556"
//               cy="1215"
//               r="0.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="0.2 0.2"
//             />
//             <circle
//               cx="556"
//               cy="1215"
//               r="4.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="0.2 0.2"
//             />
//             <circle
//               cx="556"
//               cy="1215"
//               r="5.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="0.3 0.3"
//             />
//             <circle
//               cx="556"
//               cy="1215"
//               r="6.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="0.4 0.4"
//             />
//             <circle
//               cx="556"
//               cy="1215"
//               r="7.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="0.5 0.5"
//             />
//             <circle
//               cx="556"
//               cy="1215"
//               r="8.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="0.6 0.6"
//             />
//             <circle
//               cx="556"
//               cy="1215"
//               r="9.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="0.7 0.7"
//             />
//             <circle
//               cx="556"
//               cy="1215"
//               r="14.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="0.7 0.7"
//             />
//             <circle
//               cx="556"
//               cy="1215"
//               r="15.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="0.8 0.8"
//             />
//             <circle
//               cx="556"
//               cy="1215"
//               r="16.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="0.9 0.9"
//             />
//             <circle
//               cx="556"
//               cy="1215"
//               r="17.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="1 1"
//             />
//             <circle
//               cx="556"
//               cy="1215"
//               r="18.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="1.1 1.1"
//             />
//             <circle
//               cx="556"
//               cy="1215"
//               r="19.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="1.2 1.2"
//             />
//             <circle
//               cx="556"
//               cy="1215"
//               r="23.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="1.6 1.6"
//             />
//             <circle
//               cx="556"
//               cy="1215"
//               r="24.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="1.7 1.7"
//             />
//             <circle
//               cx="556"
//               cy="1215"
//               r="25.95"
//               stroke="var(--primary-color)"
//               stroke-width="0.1"
//               strokeDasharray="1.8 1.8"
//             />
//           </g>
//           <g opacity="0.1">
//             <circle
//               cx="1272"
//               cy="1795"
//               r="399.333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="42.67 42.67"
//             />
//             <circle
//               cx="1272"
//               cy="1795"
//               r="386"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="37.33 37.33"
//             />
//             <circle
//               cx="1272"
//               cy="1795"
//               r="372.667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="32 32"
//             />
//             <circle
//               cx="1272"
//               cy="1795"
//               r="359.333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="26.67 26.67"
//             />
//             <circle
//               cx="1272"
//               cy="1795"
//               r="12.6667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="2.67 2.67"
//             />
//             <circle
//               cx="1272"
//               cy="1795"
//               r="66"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="2.67 2.67"
//             />
//             <circle
//               cx="1272"
//               cy="1795"
//               r="79.3333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="4 4"
//             />
//             <circle
//               cx="1272"
//               cy="1795"
//               r="92.6667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="5.33 5.33"
//             />
//             <circle
//               cx="1272"
//               cy="1795"
//               r="106"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="6.67 6.67"
//             />
//             <circle
//               cx="1272"
//               cy="1795"
//               r="119.333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="8 8"
//             />
//             <circle
//               cx="1272"
//               cy="1795"
//               r="132.667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="9.33 9.33"
//             />
//             <circle
//               cx="1272"
//               cy="1795"
//               r="199.333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="9.33 9.33"
//             />
//             <circle
//               cx="1272"
//               cy="1795"
//               r="212.667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="10.67 10.67"
//             />
//             <circle
//               cx="1272"
//               cy="1795"
//               r="226"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="12 12"
//             />
//             <circle
//               cx="1272"
//               cy="1795"
//               r="239.333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="13.33 13.33"
//             />
//             <circle
//               cx="1272"
//               cy="1795"
//               r="252.667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="14.67 14.67"
//             />
//             <circle
//               cx="1272"
//               cy="1795"
//               r="266"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="16 16"
//             />
//             <circle
//               cx="1272"
//               cy="1795"
//               r="319.333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="21.33 21.33"
//             />
//             <circle
//               cx="1272"
//               cy="1795"
//               r="332.667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="22.67 22.67"
//             />
//             <circle
//               cx="1272"
//               cy="1795"
//               r="346"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="24 24"
//             />
//           </g>
//           <g opacity="0.15">
//             <circle
//               cx="1059"
//               cy="899"
//               r="99.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="10.67 10.67"
//             />
//             <circle
//               cx="1059"
//               cy="899"
//               r="96.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="9.33 9.33"
//             />
//             <circle
//               cx="1059"
//               cy="899"
//               r="93.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="8 8"
//             />
//             <circle
//               cx="1059"
//               cy="899"
//               r="89.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="6.67 6.67"
//             />
//             <circle
//               cx="1059"
//               cy="899"
//               r="3.16667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="0.67 0.67"
//             />
//             <circle
//               cx="1059"
//               cy="899"
//               r="16.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="0.67 0.67"
//             />
//             <circle
//               cx="1059"
//               cy="899"
//               r="19.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="1 1"
//             />
//             <circle
//               cx="1059"
//               cy="899"
//               r="23.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="1.33 1.33"
//             />
//             <circle
//               cx="1059"
//               cy="899"
//               r="26.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="1.67 1.67"
//             />
//             <circle
//               cx="1059"
//               cy="899"
//               r="29.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2 2"
//             />
//             <circle
//               cx="1059"
//               cy="899"
//               r="33.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2.33 2.33"
//             />
//             <circle
//               cx="1059"
//               cy="899"
//               r="49.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2.33 2.33"
//             />
//             <circle
//               cx="1059"
//               cy="899"
//               r="53.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2.67 2.67"
//             />
//             <circle
//               cx="1059"
//               cy="899"
//               r="56.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="3 3"
//             />
//             <circle
//               cx="1059"
//               cy="899"
//               r="59.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="3.33 3.33"
//             />
//             <circle
//               cx="1059"
//               cy="899"
//               r="63.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="3.67 3.67"
//             />
//             <circle
//               cx="1059"
//               cy="899"
//               r="66.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="4 4"
//             />
//             <circle
//               cx="1059"
//               cy="899"
//               r="79.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="5.33 5.33"
//             />
//             <circle
//               cx="1059"
//               cy="899"
//               r="83.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="5.67 5.67"
//             />
//             <circle
//               cx="1059"
//               cy="899"
//               r="86.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="6 6"
//             />
//           </g>
//           <g opacity="0.15">
//             <circle
//               cx="457"
//               cy="260"
//               r="99.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="10.67 10.67"
//             />
//             <circle
//               cx="457.001"
//               cy="260"
//               r="96.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="9.33 9.33"
//             />
//             <circle
//               cx="456.999"
//               cy="260"
//               r="93.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="8 8"
//             />
//             <circle
//               cx="457"
//               cy="260"
//               r="89.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="6.67 6.67"
//             />
//             <circle
//               cx="456.999"
//               cy="260"
//               r="3.16667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="0.67 0.67"
//             />
//             <circle
//               cx="457.001"
//               cy="260"
//               r="16.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="0.67 0.67"
//             />
//             <circle
//               cx="457"
//               cy="260"
//               r="19.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="1 1"
//             />
//             <circle
//               cx="456.999"
//               cy="260"
//               r="23.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="1.33 1.33"
//             />
//             <circle
//               cx="457.001"
//               cy="260"
//               r="26.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="1.67 1.67"
//             />
//             <circle
//               cx="457"
//               cy="260"
//               r="29.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2 2"
//             />
//             <circle
//               cx="456.999"
//               cy="260"
//               r="33.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2.33 2.33"
//             />
//             <circle
//               cx="457"
//               cy="260"
//               r="49.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2.33 2.33"
//             />
//             <circle
//               cx="456.999"
//               cy="260"
//               r="53.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2.67 2.67"
//             />
//             <circle
//               cx="457.001"
//               cy="260"
//               r="56.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="3 3"
//             />
//             <circle
//               cx="457"
//               cy="260"
//               r="59.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="3.33 3.33"
//             />
//             <circle
//               cx="456.999"
//               cy="260"
//               r="63.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="3.67 3.67"
//             />
//             <circle
//               cx="457.001"
//               cy="260"
//               r="66.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="4 4"
//             />
//             <circle
//               cx="457"
//               cy="260"
//               r="79.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="5.33 5.33"
//             />
//             <circle
//               cx="456.999"
//               cy="260"
//               r="83.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="5.67 5.67"
//             />
//             <circle
//               cx="457.001"
//               cy="260"
//               r="86.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="6 6"
//             />
//           </g>
//           <g opacity="0.15">
//             <circle
//               cx="417"
//               cy="2395"
//               r="199.667"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="21.33 21.33"
//             />
//             <circle
//               cx="417.001"
//               cy="2395"
//               r="193"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="18.67 18.67"
//             />
//             <circle
//               cx="416.999"
//               cy="2395"
//               r="186.333"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="16 16"
//             />
//             <circle
//               cx="417"
//               cy="2395"
//               r="179.667"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="13.33 13.33"
//             />
//             <circle
//               cx="416.999"
//               cy="2395"
//               r="6.33333"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="1.33 1.33"
//             />
//             <circle
//               cx="417.001"
//               cy="2395"
//               r="33"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="1.33 1.33"
//             />
//             <circle
//               cx="417"
//               cy="2395"
//               r="39.6667"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="2 2"
//             />
//             <circle
//               cx="416.999"
//               cy="2395"
//               r="46.3333"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="2.67 2.67"
//             />
//             <circle
//               cx="417.001"
//               cy="2395"
//               r="53"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="3.33 3.33"
//             />
//             <circle
//               cx="417"
//               cy="2395"
//               r="59.6667"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="4 4"
//             />
//             <circle
//               cx="416.999"
//               cy="2395"
//               r="66.3333"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="4.67 4.67"
//             />
//             <circle
//               cx="417"
//               cy="2395"
//               r="99.6667"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="4.67 4.67"
//             />
//             <circle
//               cx="416.999"
//               cy="2395"
//               r="106.333"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="5.33 5.33"
//             />
//             <circle
//               cx="417.001"
//               cy="2395"
//               r="113"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="6 6"
//             />
//             <circle
//               cx="417"
//               cy="2395"
//               r="119.667"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="6.67 6.67"
//             />
//             <circle
//               cx="416.999"
//               cy="2395"
//               r="126.333"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="7.33 7.33"
//             />
//             <circle
//               cx="417.001"
//               cy="2395"
//               r="133"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="8 8"
//             />
//             <circle
//               cx="417"
//               cy="2395"
//               r="159.667"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="10.67 10.67"
//             />
//             <circle
//               cx="416.999"
//               cy="2395"
//               r="166.333"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="11.33 11.33"
//             />
//             <circle
//               cx="417.001"
//               cy="2395"
//               r="173"
//               stroke="var(--primary-color)"
//               stroke-width="0.666667"
//               strokeDasharray="12 12"
//             />
//           </g>
//           <g opacity="0.1">
//             <circle
//               cx="-64"
//               cy="3239"
//               r="399.333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="42.67 42.67"
//             />
//             <circle
//               cx="-63.9974"
//               cy="3239"
//               r="386"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="37.33 37.33"
//             />
//             <circle
//               cx="-64.0026"
//               cy="3239"
//               r="372.667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="32 32"
//             />
//             <circle
//               cx="-64"
//               cy="3239"
//               r="359.333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="26.67 26.67"
//             />
//             <circle
//               cx="-63.9974"
//               cy="3239"
//               r="66"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="2.67 2.67"
//             />
//             <circle
//               cx="-64"
//               cy="3239"
//               r="79.3333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="4 4"
//             />
//             <circle
//               cx="-64.0026"
//               cy="3239"
//               r="92.6667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="5.33 5.33"
//             />
//             <circle
//               cx="-63.9974"
//               cy="3239"
//               r="106"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="6.67 6.67"
//             />
//             <circle
//               cx="-64"
//               cy="3239"
//               r="119.333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="8 8"
//             />
//             <circle
//               cx="-64.0026"
//               cy="3239"
//               r="132.667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="9.33 9.33"
//             />
//             <circle
//               cx="-64"
//               cy="3239"
//               r="199.333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="9.33 9.33"
//             />
//             <circle
//               cx="-64.0026"
//               cy="3239"
//               r="212.667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="10.67 10.67"
//             />
//             <circle
//               cx="-63.9974"
//               cy="3239"
//               r="226"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="12 12"
//             />
//             <circle
//               cx="-64"
//               cy="3239"
//               r="239.333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="13.33 13.33"
//             />
//             <circle
//               cx="-64.0026"
//               cy="3239"
//               r="252.667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="14.67 14.67"
//             />
//             <circle
//               cx="-63.9974"
//               cy="3239"
//               r="266"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="16 16"
//             />
//             <circle
//               cx="-64"
//               cy="3239"
//               r="319.333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="21.33 21.33"
//             />
//             <circle
//               cx="-64.0026"
//               cy="3239"
//               r="332.667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="22.67 22.67"
//             />
//             <circle
//               cx="-63.9974"
//               cy="3239"
//               r="346"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="24 24"
//             />
//           </g>
//           <g opacity="0.18">
//             <circle
//               cx="794"
//               cy="2966"
//               r="99.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="10.67 10.67"
//             />
//             <circle
//               cx="794.001"
//               cy="2966"
//               r="96.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="9.33 9.33"
//             />
//             <circle
//               cx="793.999"
//               cy="2966"
//               r="93.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="8 8"
//             />
//             <circle
//               cx="794"
//               cy="2966"
//               r="89.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="6.67 6.67"
//             />
//             <circle
//               cx="793.999"
//               cy="2966"
//               r="3.16667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="0.67 0.67"
//             />
//             <circle
//               cx="794.001"
//               cy="2966"
//               r="16.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="0.67 0.67"
//             />
//             <circle
//               cx="794"
//               cy="2966"
//               r="19.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="1 1"
//             />
//             <circle
//               cx="793.999"
//               cy="2966"
//               r="23.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="1.33 1.33"
//             />
//             <circle
//               cx="794.001"
//               cy="2966"
//               r="26.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="1.67 1.67"
//             />
//             <circle
//               cx="794"
//               cy="2966"
//               r="29.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2 2"
//             />
//             <circle
//               cx="793.999"
//               cy="2966"
//               r="33.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2.33 2.33"
//             />
//             <circle
//               cx="794"
//               cy="2966"
//               r="49.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2.33 2.33"
//             />
//             <circle
//               cx="793.999"
//               cy="2966"
//               r="53.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2.67 2.67"
//             />
//             <circle
//               cx="794.001"
//               cy="2966"
//               r="56.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="3 3"
//             />
//             <circle
//               cx="794"
//               cy="2966"
//               r="59.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="3.33 3.33"
//             />
//             <circle
//               cx="793.999"
//               cy="2966"
//               r="63.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="3.67 3.67"
//             />
//             <circle
//               cx="794.001"
//               cy="2966"
//               r="66.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="4 4"
//             />
//             <circle
//               cx="794"
//               cy="2966"
//               r="79.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="5.33 5.33"
//             />
//             <circle
//               cx="793.999"
//               cy="2966"
//               r="83.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="5.67 5.67"
//             />
//             <circle
//               cx="794.001"
//               cy="2966"
//               r="86.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="6 6"
//             />
//           </g>
//           <g opacity="0.18">
//             <circle
//               cx="570"
//               cy="3720"
//               r="99.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="10.67 10.67"
//             />
//             <circle
//               cx="570.001"
//               cy="3720"
//               r="96.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="9.33 9.33"
//             />
//             <circle
//               cx="569.999"
//               cy="3720"
//               r="93.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="8 8"
//             />
//             <circle
//               cx="570"
//               cy="3720"
//               r="89.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="6.67 6.67"
//             />
//             <circle
//               cx="569.999"
//               cy="3720"
//               r="3.16667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="0.67 0.67"
//             />
//             <circle
//               cx="570.001"
//               cy="3720"
//               r="16.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="0.67 0.67"
//             />
//             <circle
//               cx="570"
//               cy="3720"
//               r="19.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="1 1"
//             />
//             <circle
//               cx="569.999"
//               cy="3720"
//               r="23.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="1.33 1.33"
//             />
//             <circle
//               cx="570.001"
//               cy="3720"
//               r="26.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="1.67 1.67"
//             />
//             <circle
//               cx="570"
//               cy="3720"
//               r="29.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2 2"
//             />
//             <circle
//               cx="569.999"
//               cy="3720"
//               r="33.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2.33 2.33"
//             />
//             <circle
//               cx="570"
//               cy="3720"
//               r="49.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2.33 2.33"
//             />
//             <circle
//               cx="569.999"
//               cy="3720"
//               r="53.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2.67 2.67"
//             />
//             <circle
//               cx="570.001"
//               cy="3720"
//               r="56.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="3 3"
//             />
//             <circle
//               cx="570"
//               cy="3720"
//               r="59.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="3.33 3.33"
//             />
//             <circle
//               cx="569.999"
//               cy="3720"
//               r="63.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="3.67 3.67"
//             />
//             <circle
//               cx="570.001"
//               cy="3720"
//               r="66.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="4 4"
//             />
//             <circle
//               cx="570"
//               cy="3720"
//               r="79.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="5.33 5.33"
//             />
//             <circle
//               cx="569.999"
//               cy="3720"
//               r="83.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="5.67 5.67"
//             />
//             <circle
//               cx="570.001"
//               cy="3720"
//               r="86.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="6 6"
//             />
//           </g>
//           <g opacity="0.18">
//             <circle
//               cx="447"
//               cy="6035"
//               r="99.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="10.67 10.67"
//             />
//             <circle
//               cx="447.001"
//               cy="6035"
//               r="96.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="9.33 9.33"
//             />
//             <circle
//               cx="446.999"
//               cy="6035"
//               r="93.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="8 8"
//             />
//             <circle
//               cx="447"
//               cy="6035"
//               r="89.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="6.67 6.67"
//             />
//             <circle
//               cx="446.999"
//               cy="6035"
//               r="3.16667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="0.67 0.67"
//             />
//             <circle
//               cx="447.001"
//               cy="6035"
//               r="16.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="0.67 0.67"
//             />
//             <circle
//               cx="447"
//               cy="6035"
//               r="19.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="1 1"
//             />
//             <circle
//               cx="446.999"
//               cy="6035"
//               r="23.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="1.33 1.33"
//             />
//             <circle
//               cx="447.001"
//               cy="6035"
//               r="26.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="1.67 1.67"
//             />
//             <circle
//               cx="447"
//               cy="6035"
//               r="29.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2 2"
//             />
//             <circle
//               cx="446.999"
//               cy="6035"
//               r="33.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2.33 2.33"
//             />
//             <circle
//               cx="447"
//               cy="6035"
//               r="49.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2.33 2.33"
//             />
//             <circle
//               cx="446.999"
//               cy="6035"
//               r="53.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="2.67 2.67"
//             />
//             <circle
//               cx="447.001"
//               cy="6035"
//               r="56.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="3 3"
//             />
//             <circle
//               cx="447"
//               cy="6035"
//               r="59.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="3.33 3.33"
//             />
//             <circle
//               cx="446.999"
//               cy="6035"
//               r="63.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="3.67 3.67"
//             />
//             <circle
//               cx="447.001"
//               cy="6035"
//               r="66.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="4 4"
//             />
//             <circle
//               cx="447"
//               cy="6035"
//               r="79.8333"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="5.33 5.33"
//             />
//             <circle
//               cx="446.999"
//               cy="6035"
//               r="83.1667"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="5.67 5.67"
//             />
//             <circle
//               cx="447.001"
//               cy="6035"
//               r="86.5"
//               stroke="var(--primary-color)"
//               stroke-width="0.333333"
//               strokeDasharray="6 6"
//             />
//           </g>
//           <g opacity="0.1">
//             <circle
//               cx="1225"
//               cy="4120"
//               r="299.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="32 32"
//             />
//             <circle
//               cx="1225"
//               cy="4120"
//               r="289.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="28 28"
//             />
//             <circle
//               cx="1225"
//               cy="4120"
//               r="279.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="24 24"
//             />
//             <circle
//               cx="1225"
//               cy="4120"
//               r="269.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="20 20"
//             />
//             <circle
//               cx="1225"
//               cy="4120"
//               r="9.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="2 2"
//             />
//             <circle
//               cx="1225"
//               cy="4120"
//               r="49.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="2 2"
//             />
//             <circle
//               cx="1225"
//               cy="4120"
//               r="59.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="3 3"
//             />
//             <circle
//               cx="1225"
//               cy="4120"
//               r="69.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="4 4"
//             />
//             <circle
//               cx="1225"
//               cy="4120"
//               r="79.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="5 5"
//             />
//             <circle
//               cx="1225"
//               cy="4120"
//               r="89.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="6 6"
//             />
//             <circle
//               cx="1225"
//               cy="4120"
//               r="99.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="7 7"
//             />
//             <circle
//               cx="1225"
//               cy="4120"
//               r="149.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="7 7"
//             />
//             <circle
//               cx="1225"
//               cy="4120"
//               r="159.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="8 8"
//             />
//             <circle
//               cx="1225"
//               cy="4120"
//               r="169.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="9 9"
//             />
//             <circle
//               cx="1225"
//               cy="4120"
//               r="179.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="10 10"
//             />
//             <circle
//               cx="1225"
//               cy="4120"
//               r="189.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="11 11"
//             />
//             <circle
//               cx="1225"
//               cy="4120"
//               r="199.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="12 12"
//             />
//             <circle
//               cx="1225"
//               cy="4120"
//               r="239.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="16 16"
//             />
//             <circle
//               cx="1225"
//               cy="4120"
//               r="249.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="17 17"
//             />
//             <circle
//               cx="1225"
//               cy="4120"
//               r="259.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="18 18"
//             />
//           </g>
//           <g opacity="0.1">
//             <circle
//               cx="1245"
//               cy="6135"
//               r="299.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="32 32"
//             />
//             <circle
//               cx="1245"
//               cy="6135"
//               r="289.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="28 28"
//             />
//             <circle
//               cx="1245"
//               cy="6135"
//               r="279.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="24 24"
//             />
//             <circle
//               cx="1245"
//               cy="6135"
//               r="269.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="20 20"
//             />
//             <circle
//               cx="1245"
//               cy="6135"
//               r="9.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="2 2"
//             />
//             <circle
//               cx="1245"
//               cy="6135"
//               r="49.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="2 2"
//             />
//             <circle
//               cx="1245"
//               cy="6135"
//               r="59.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="3 3"
//             />
//             <circle
//               cx="1245"
//               cy="6135"
//               r="69.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="4 4"
//             />
//             <circle
//               cx="1245"
//               cy="6135"
//               r="79.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="5 5"
//             />
//             <circle
//               cx="1245"
//               cy="6135"
//               r="89.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="6 6"
//             />
//             <circle
//               cx="1245"
//               cy="6135"
//               r="99.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="7 7"
//             />
//             <circle
//               cx="1245"
//               cy="6135"
//               r="149.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="7 7"
//             />
//             <circle
//               cx="1245"
//               cy="6135"
//               r="159.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="8 8"
//             />
//             <circle
//               cx="1245"
//               cy="6135"
//               r="169.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="9 9"
//             />
//             <circle
//               cx="1245"
//               cy="6135"
//               r="179.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="10 10"
//             />
//             <circle
//               cx="1245"
//               cy="6135"
//               r="189.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="11 11"
//             />
//             <circle
//               cx="1245"
//               cy="6135"
//               r="199.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="12 12"
//             />
//             <circle
//               cx="1245"
//               cy="6135"
//               r="239.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="16 16"
//             />
//             <circle
//               cx="1245"
//               cy="6135"
//               r="249.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="17 17"
//             />
//             <circle
//               cx="1245"
//               cy="6135"
//               r="259.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="18 18"
//             />
//           </g>
//           <g opacity="0.1">
//             <circle
//               cx="466"
//               cy="6882"
//               r="299.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="32 32"
//             />
//             <circle
//               cx="466"
//               cy="6882"
//               r="289.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="28 28"
//             />
//             <circle
//               cx="466"
//               cy="6882"
//               r="279.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="24 24"
//             />
//             <circle
//               cx="466"
//               cy="6882"
//               r="269.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="20 20"
//             />
//             <circle
//               cx="466"
//               cy="6882"
//               r="159.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="8 8"
//             />
//             <circle
//               cx="466"
//               cy="6882"
//               r="169.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="9 9"
//             />
//             <circle
//               cx="466"
//               cy="6882"
//               r="179.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="10 10"
//             />
//             <circle
//               cx="466"
//               cy="6882"
//               r="189.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="11 11"
//             />
//             <circle
//               cx="466"
//               cy="6882"
//               r="199.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="12 12"
//             />
//             <circle
//               cx="466"
//               cy="6882"
//               r="239.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="16 16"
//             />
//             <circle
//               cx="466"
//               cy="6882"
//               r="249.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="17 17"
//             />
//             <circle
//               cx="466"
//               cy="6882"
//               r="259.5"
//               stroke="var(--primary-color)"
//               strokeDasharray="18 18"
//             />
//           </g>
//           <g opacity="0.2">
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="47.4208"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="5.07 5.07"
//             />
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="45.8375"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="4.43 4.43"
//             />
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="44.2542"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="3.8 3.8"
//             />
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="42.6708"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="3.17 3.17"
//             />
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="1.50417"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="0.32 0.32"
//             />
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="7.8375"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="0.32 0.32"
//             />
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="9.42083"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="0.47 0.47"
//             />
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="11.0042"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="0.63 0.63"
//             />
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="12.5875"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="0.79 0.79"
//             />
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="14.1708"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="0.95 0.95"
//             />
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="15.7542"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="1.11 1.11"
//             />
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="23.6708"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="1.11 1.11"
//             />
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="25.2542"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="1.27 1.27"
//             />
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="26.8375"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="1.42 1.42"
//             />
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="28.4208"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="1.58 1.58"
//             />
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="30.0042"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="1.74 1.74"
//             />
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="31.5875"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="1.9 1.9"
//             />
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="37.9208"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="2.53 2.53"
//             />
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="39.5042"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="2.69 2.69"
//             />
//             <circle
//               cx="1148.5"
//               cy="3371.5"
//               r="41.0875"
//               stroke="var(--primary-color)"
//               stroke-width="0.158333"
//               strokeDasharray="2.85 2.85"
//             />
//           </g>
//           <g opacity="0.1">
//             <circle
//               cx="299"
//               cy="5123"
//               r="399.333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="42.67 42.67"
//             />
//             <circle
//               cx="299.003"
//               cy="5123"
//               r="386"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="37.33 37.33"
//             />
//             <circle
//               cx="298.997"
//               cy="5123"
//               r="372.667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="32 32"
//             />
//             <circle
//               cx="299"
//               cy="5123"
//               r="359.333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="26.67 26.67"
//             />
//             <circle
//               cx="298.997"
//               cy="5123"
//               r="12.6667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="2.67 2.67"
//             />
//             <circle
//               cx="299.003"
//               cy="5123"
//               r="66"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="2.67 2.67"
//             />
//             <circle
//               cx="299"
//               cy="5123"
//               r="79.3333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="4 4"
//             />
//             <circle
//               cx="298.997"
//               cy="5123"
//               r="92.6667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="5.33 5.33"
//             />
//             <circle
//               cx="299.003"
//               cy="5123"
//               r="106"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="6.67 6.67"
//             />
//             <circle
//               cx="299"
//               cy="5123"
//               r="119.333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="8 8"
//             />
//             <circle
//               cx="298.997"
//               cy="5123"
//               r="132.667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="9.33 9.33"
//             />
//             <circle
//               cx="299"
//               cy="5123"
//               r="199.333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="9.33 9.33"
//             />
//             <circle
//               cx="298.997"
//               cy="5123"
//               r="212.667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="10.67 10.67"
//             />
//             <circle
//               cx="299.003"
//               cy="5123"
//               r="226"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="12 12"
//             />
//             <circle
//               cx="299"
//               cy="5123"
//               r="239.333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="13.33 13.33"
//             />
//             <circle
//               cx="298.997"
//               cy="5123"
//               r="252.667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="14.67 14.67"
//             />
//             <circle
//               cx="299.003"
//               cy="5123"
//               r="266"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="16 16"
//             />
//             <circle
//               cx="299"
//               cy="5123"
//               r="319.333"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="21.33 21.33"
//             />
//             <circle
//               cx="298.997"
//               cy="5123"
//               r="332.667"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="22.67 22.67"
//             />
//             <circle
//               cx="299.003"
//               cy="5123"
//               r="346"
//               stroke="var(--primary-color)"
//               stroke-width="1.33333"
//               strokeDasharray="24 24"
//             />
//           </g>
//         </g>
//         <defs>
//           <clipPath id="clip0_1118_159680">
//             <rect width="100%" height={height} fill="white" />
//           </clipPath>
//         </defs>
//       </svg>
//     </div>
//   );
// };

export const Rounded = () => {
  return (
    <svg
      width="100%"
      height="auto"
      viewBox="0 0 480 480"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g filter="url(#filter0_d_2315_113242)">
        <circle cx="240" cy="236" r="200" fill="var(--primary-color)" />
      </g>
      <g opacity="0.3">
        <circle
          cx="240"
          cy="236"
          r="199.667"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="21.33 21.33"
        />
        <circle
          cx="240.001"
          cy="236"
          r="193"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="18.67 18.67"
        />
        <circle
          cx="239.999"
          cy="236"
          r="186.333"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="16 16"
        />
        <circle
          cx="240"
          cy="236"
          r="179.667"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="13.33 13.33"
        />
        <circle
          cx="239.999"
          cy="236"
          r="6.33333"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="1.33 1.33"
        />
        <circle
          cx="240.001"
          cy="236"
          r="33"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="1.33 1.33"
        />
        <circle
          cx="240"
          cy="236"
          r="39.6667"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="2 2"
        />
        <circle
          cx="239.999"
          cy="236"
          r="46.3333"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="2.67 2.67"
        />
        <circle
          cx="240.001"
          cy="236"
          r="53"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="3.33 3.33"
        />
        <circle
          cx="240"
          cy="236"
          r="59.6667"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="4 4"
        />
        <circle
          cx="239.999"
          cy="236"
          r="66.3333"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="4.67 4.67"
        />
        <circle
          cx="240"
          cy="236"
          r="99.6667"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="4.67 4.67"
        />
        <circle
          cx="239.999"
          cy="236"
          r="106.333"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="5.33 5.33"
        />
        <circle
          cx="240.001"
          cy="236"
          r="113"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="6 6"
        />
        <circle
          cx="240"
          cy="236"
          r="119.667"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="6.67 6.67"
        />
        <circle
          cx="239.999"
          cy="236"
          r="126.333"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="7.33 7.33"
        />
        <circle
          cx="240.001"
          cy="236"
          r="133"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="8 8"
        />
        <circle
          cx="240"
          cy="236"
          r="159.667"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="10.67 10.67"
        />
        <circle
          cx="239.999"
          cy="236"
          r="166.333"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="11.33 11.33"
        />
        <circle
          cx="240.001"
          cy="236"
          r="173"
          stroke="white"
          stroke-width="0.666667"
          strokeDasharray="12 12"
        />
      </g>
      <defs>
        <filter
          id="filter0_d_2315_113242"
          x="0"
          y="0"
          width="480"
          height="480"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="4" />
          <feGaussianBlur stdDeviation="20" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0.238 0 0 0 0 0.68 0 0 0 0.2 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_2315_113242"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_2315_113242"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
};

export const Bg4 = () => {
  const height = useBodyHeight();
  return (
    <div
      className="absolute sm:hidden pointer-events-none z-[-6] block top-0 left-0 mx-auto  w-full"
      style={{
        height,
        backgroundColor: "rgba(var(--primary-color-rgb),0.1)",
      }}
    >
      <svg
        width="100%"
        className="max-w-[2000px] mx-auto"
        height={height}
        viewBox="0 0 1440 6249"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_1269_174429)">
          <g opacity="0.1" filter="url(#filter0_d_1269_174429)">
            <rect
              x="-518"
              y="1203"
              width="100%"
              height="1380"
              rx="690"
              fill="var(--primary-color)"
              fillOpacity="0.1"
              shapeRendering="crispEdges"
            />
          </g>
          <g opacity="0.08" filter="url(#filter1_d_1269_174429)">
            <rect
              x="1058"
              y="1893"
              width="475"
              height="475"
              rx="237.5"
              fill="var(--primary-color)"
              fillOpacity="0.1"
              shapeRendering="crispEdges"
            />
          </g>
          <g opacity="0.08" filter="url(#filter2_d_1269_174429)">
            <rect
              x="881"
              y="732"
              width="720"
              height="720"
              rx="360"
              fill="var(--primary-color)"
              fillOpacity="0.1"
              shapeRendering="crispEdges"
            />
          </g>
          <g opacity="0.1" filter="url(#filter3_d_1269_174429)">
            <rect
              x="-518"
              y="4569"
              width="100%"
              height="1380"
              rx="690"
              fill="var(--primary-color)"
              fillOpacity="0.1"
              shapeRendering="crispEdges"
            />
          </g>
          <g opacity="0.1" filter="url(#filter4_d_1269_174429)">
            <rect
              x="425"
              y="3662"
              width="695"
              height="1380"
              rx="347.5"
              fill="var(--primary-color)"
              fillOpacity="0.1"
              shapeRendering="crispEdges"
            />
          </g>
          <g opacity="0.1" filter="url(#filter5_d_1269_174429)">
            <rect
              x="425"
              y="1886"
              width="695"
              height="1380"
              rx="347.5"
              fill="var(--primary-color)"
              fillOpacity="0.1"
              shapeRendering="crispEdges"
            />
          </g>
          <g opacity="0.1" filter="url(#filter6_d_1269_174429)">
            <rect
              x="425"
              y="30"
              width="695"
              height="1380"
              rx="347.5"
              fill="var(--primary-color)"
              fillOpacity="0.1"
              shapeRendering="crispEdges"
            />
          </g>
          <g opacity="0.1" filter="url(#filter7_d_1269_174429)">
            <rect
              x="763"
              y="2659"
              width="100%"
              height="1380"
              rx="690"
              fill="var(--primary-color)"
              fillOpacity="0.1"
              shapeRendering="crispEdges"
            />
          </g>
        </g>
        <defs>
          <filter
            id="filter0_d_1269_174429"
            x="-1398.1"
            y="336.98"
            width="100%"
            height="3140.2"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="14.0816" />
            <feGaussianBlur stdDeviation="440.051" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.486275 0 0 0 0 0 0 0 0 0 0.678431 0 0 0 0.6 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_1269_174429"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_1269_174429"
              result="shape"
            />
          </filter>
          <filter
            id="filter1_d_1269_174429"
            x="755.066"
            y="1594.91"
            width="100%"
            height="1080.87"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="4.84694" />
            <feGaussianBlur stdDeviation="151.467" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.486275 0 0 0 0 0 0 0 0 0 0.678431 0 0 0 0.6 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_1269_174429"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_1269_174429"
              result="shape"
            />
          </filter>
          <filter
            id="filter2_d_1269_174429"
            x="421.816"
            y="280.163"
            width="100%"
            height="1638.37"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="7.34694" />
            <feGaussianBlur stdDeviation="229.592" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.486275 0 0 0 0 0 0 0 0 0 0.678431 0 0 0 0.6 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_1269_174429"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_1269_174429"
              result="shape"
            />
          </filter>
          <filter
            id="filter3_d_1269_174429"
            x="-1398.1"
            y="3702.98"
            width="100%"
            height="3140.2"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="14.0816" />
            <feGaussianBlur stdDeviation="440.051" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.486275 0 0 0 0 0 0 0 0 0 0.678431 0 0 0 0.6 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_1269_174429"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_1269_174429"
              result="shape"
            />
          </filter>
          <filter
            id="filter4_d_1269_174429"
            x="-455.102"
            y="2795.98"
            width="100%"
            height="3140.2"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="14.0816" />
            <feGaussianBlur stdDeviation="440.051" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.486275 0 0 0 0 0 0 0 0 0 0.678431 0 0 0 0.6 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_1269_174429"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_1269_174429"
              result="shape"
            />
          </filter>
          <filter
            id="filter5_d_1269_174429"
            x="-455.102"
            y="1019.98"
            width="100%"
            height="3140.2"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="14.0816" />
            <feGaussianBlur stdDeviation="440.051" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.486275 0 0 0 0 0 0 0 0 0 0.678431 0 0 0 0.6 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_1269_174429"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_1269_174429"
              result="shape"
            />
          </filter>
          <filter
            id="filter6_d_1269_174429"
            x="-455.102"
            y="-836.02"
            width="2455.2"
            height="3140.2"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="14.0816" />
            <feGaussianBlur stdDeviation="440.051" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.486275 0 0 0 0 0 0 0 0 0 0.678431 0 0 0 0.6 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_1269_174429"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_1269_174429"
              result="shape"
            />
          </filter>
          <filter
            id="filter7_d_1269_174429"
            x="-117.102"
            y="1792.98"
            width="3140.2"
            height="3140.2"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="14.0816" />
            <feGaussianBlur stdDeviation="440.051" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.486275 0 0 0 0 0 0 0 0 0 0.678431 0 0 0 0.6 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_1269_174429"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_1269_174429"
              result="shape"
            />
          </filter>
          <clipPath id="clip0_1269_174429">
            <rect width="100%" height="6249" fill="white" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
};

// export const Bg4 = () => {
//   const height = useBodyHeight();
//   return (
//     <div
//       className="absolute pointer-events-none z-[-6] block top-0 left-0 mx-auto  w-full"
//       style={{
//         height,
//       }}
//     >
//       <svg
//         width="100%"
//         className="max-w-[2000px] mx-auto"
//         height={height}
//         viewBox={`0 0 100% 100%`}
//         fill="none"
//         xmlns="http://www.w3.org/2000/svg"
//       >
//         <g clipPath="url(#clip0_1269_174429)">
//           {/* <rect width="100%" height={height} fill="#FEFAFF" /> */}
//           <rect width="100%" height={height} fill="#FEFAFF" />
//           <g opacity="0.1" filter="url(#filter0_d_1269_174429)">
//             <rect
//               x="-518"
//               y="1203"
//               width="1380"
//               height="1380"
//               rx="690"
//               fill="var(--primary-color)"
//               fillOpacity="0.1"
//               shapeRendering="crispEdges"
//             />
//           </g>
//           <g opacity="0.08" filter="url(#filter1_d_1269_174429)">
//             <rect
//               x="1058"
//               y="1893"
//               width="475"
//               height="475"
//               rx="237.5"
//               fill="var(--primary-color)"
//               fillOpacity="0.1"
//               shapeRendering="crispEdges"
//             />
//           </g>
//           <g opacity="0.08" filter="url(#filter2_d_1269_174429)">
//             <rect
//               x="881"
//               y="732"
//               width="720"
//               height="720"
//               rx="360"
//               fill="var(--primary-color)"
//               fillOpacity="0.1"
//               shapeRendering="crispEdges"
//             />
//           </g>
//           <g opacity="0.1" filter="url(#filter3_d_1269_174429)">
//             <rect
//               x="-518"
//               y="4569"
//               width="1380"
//               height="1380"
//               rx="690"
//               fill="var(--primary-color)"
//               fillOpacity="0.1"
//               shapeRendering="crispEdges"
//             />
//           </g>
//           <g opacity="0.1" filter="url(#filter4_d_1269_174429)">
//             <rect
//               x="425"
//               y="3662"
//               width="695"
//               height="1380"
//               rx="347.5"
//               fill="var(--primary-color)"
//               fillOpacity="0.1"
//               shapeRendering="crispEdges"
//             />
//           </g>
//           <g opacity="0.1" filter="url(#filter5_d_1269_174429)">
//             <rect
//               x="425"
//               y="1886"
//               width="695"
//               height="1380"
//               rx="347.5"
//               fill="var(--primary-color)"
//               fillOpacity="0.1"
//               shapeRendering="crispEdges"
//             />
//           </g>
//           <g opacity="0.1" filter="url(#filter6_d_1269_174429)">
//             <rect
//               x="425"
//               y="30"
//               width="695"
//               height="1380"
//               rx="347.5"
//               fill="var(--primary-color)"
//               fillOpacity="0.1"
//               shapeRendering="crispEdges"
//             />
//           </g>
//           <g opacity="0.1" filter="url(#filter7_d_1269_174429)">
//             <rect
//               x="763"
//               y="2659"
//               width="1380"
//               height="1380"
//               rx="690"
//               fill="var(--primary-color)"
//               fillOpacity="0.1"
//               shapeRendering="crispEdges"
//             />
//           </g>
//         </g>
//         <defs>
//           <filter
//             id="filter0_d_1269_174429"
//             x="-1398.1"
//             y="336.98"
//             width="3140.2"
//             height="3140.2"
//             filterUnits="userSpaceOnUse"
//             color-interpolation-filters="sRGB"
//           >
//             <feFlood flood-opacity="0" result="BackgroundImageFix" />
//             <feColorMatrix
//               in="SourceAlpha"
//               type="matrix"
//               values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
//               result="hardAlpha"
//             />
//             <feOffset dy="14.0816" />
//             <feGaussianBlur stdDeviation="440.051" />
//             <feComposite in2="hardAlpha" operator="out" />
//             <feColorMatrix
//               type="matrix"
//               values="0 0 0 0 0.486275 0 0 0 0 0 0 0 0 0 0.678431 0 0 0 0.6 0"
//             />
//             <feBlend
//               mode="normal"
//               in2="BackgroundImageFix"
//               result="effect1_dropShadow_1269_174429"
//             />
//             <feBlend
//               mode="normal"
//               in="SourceGraphic"
//               in2="effect1_dropShadow_1269_174429"
//               result="shape"
//             />
//           </filter>
//           <filter
//             id="filter1_d_1269_174429"
//             x="755.066"
//             y="1594.91"
//             width="1080.87"
//             height="1080.87"
//             filterUnits="userSpaceOnUse"
//             color-interpolation-filters="sRGB"
//           >
//             <feFlood flood-opacity="0" result="BackgroundImageFix" />
//             <feColorMatrix
//               in="SourceAlpha"
//               type="matrix"
//               values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
//               result="hardAlpha"
//             />
//             <feOffset dy="4.84694" />
//             <feGaussianBlur stdDeviation="151.467" />
//             <feComposite in2="hardAlpha" operator="out" />
//             <feColorMatrix
//               type="matrix"
//               values="0 0 0 0 0.486275 0 0 0 0 0 0 0 0 0 0.678431 0 0 0 0.6 0"
//             />
//             <feBlend
//               mode="normal"
//               in2="BackgroundImageFix"
//               result="effect1_dropShadow_1269_174429"
//             />
//             <feBlend
//               mode="normal"
//               in="SourceGraphic"
//               in2="effect1_dropShadow_1269_174429"
//               result="shape"
//             />
//           </filter>
//           <filter
//             id="filter2_d_1269_174429"
//             x="421.816"
//             y="280.163"
//             width="1638.37"
//             height="1638.37"
//             filterUnits="userSpaceOnUse"
//             color-interpolation-filters="sRGB"
//           >
//             <feFlood flood-opacity="0" result="BackgroundImageFix" />
//             <feColorMatrix
//               in="SourceAlpha"
//               type="matrix"
//               values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
//               result="hardAlpha"
//             />
//             <feOffset dy="7.34694" />
//             <feGaussianBlur stdDeviation="229.592" />
//             <feComposite in2="hardAlpha" operator="out" />
//             <feColorMatrix
//               type="matrix"
//               values="0 0 0 0 0.486275 0 0 0 0 0 0 0 0 0 0.678431 0 0 0 0.6 0"
//             />
//             <feBlend
//               mode="normal"
//               in2="BackgroundImageFix"
//               result="effect1_dropShadow_1269_174429"
//             />
//             <feBlend
//               mode="normal"
//               in="SourceGraphic"
//               in2="effect1_dropShadow_1269_174429"
//               result="shape"
//             />
//           </filter>
//           <filter
//             id="filter3_d_1269_174429"
//             x="-1398.1"
//             y="3702.98"
//             width="3140.2"
//             height="3140.2"
//             filterUnits="userSpaceOnUse"
//             color-interpolation-filters="sRGB"
//           >
//             <feFlood flood-opacity="0" result="BackgroundImageFix" />
//             <feColorMatrix
//               in="SourceAlpha"
//               type="matrix"
//               values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
//               result="hardAlpha"
//             />
//             <feOffset dy="14.0816" />
//             <feGaussianBlur stdDeviation="440.051" />
//             <feComposite in2="hardAlpha" operator="out" />
//             <feColorMatrix
//               type="matrix"
//               values="0 0 0 0 0.486275 0 0 0 0 0 0 0 0 0 0.678431 0 0 0 0.6 0"
//             />
//             <feBlend
//               mode="normal"
//               in2="BackgroundImageFix"
//               result="effect1_dropShadow_1269_174429"
//             />
//             <feBlend
//               mode="normal"
//               in="SourceGraphic"
//               in2="effect1_dropShadow_1269_174429"
//               result="shape"
//             />
//           </filter>
//           <filter
//             id="filter4_d_1269_174429"
//             x="-455.102"
//             y="2795.98"
//             width="2455.2"
//             height="3140.2"
//             filterUnits="userSpaceOnUse"
//             color-interpolation-filters="sRGB"
//           >
//             <feFlood flood-opacity="0" result="BackgroundImageFix" />
//             <feColorMatrix
//               in="SourceAlpha"
//               type="matrix"
//               values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
//               result="hardAlpha"
//             />
//             <feOffset dy="14.0816" />
//             <feGaussianBlur stdDeviation="440.051" />
//             <feComposite in2="hardAlpha" operator="out" />
//             <feColorMatrix
//               type="matrix"
//               values="0 0 0 0 0.486275 0 0 0 0 0 0 0 0 0 0.678431 0 0 0 0.6 0"
//             />
//             <feBlend
//               mode="normal"
//               in2="BackgroundImageFix"
//               result="effect1_dropShadow_1269_174429"
//             />
//             <feBlend
//               mode="normal"
//               in="SourceGraphic"
//               in2="effect1_dropShadow_1269_174429"
//               result="shape"
//             />
//           </filter>
//           <filter
//             id="filter5_d_1269_174429"
//             x="-455.102"
//             y="1019.98"
//             width="2455.2"
//             height="3140.2"
//             filterUnits="userSpaceOnUse"
//             color-interpolation-filters="sRGB"
//           >
//             <feFlood flood-opacity="0" result="BackgroundImageFix" />
//             <feColorMatrix
//               in="SourceAlpha"
//               type="matrix"
//               values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
//               result="hardAlpha"
//             />
//             <feOffset dy="14.0816" />
//             <feGaussianBlur stdDeviation="440.051" />
//             <feComposite in2="hardAlpha" operator="out" />
//             <feColorMatrix
//               type="matrix"
//               values="0 0 0 0 0.486275 0 0 0 0 0 0 0 0 0 0.678431 0 0 0 0.6 0"
//             />
//             <feBlend
//               mode="normal"
//               in2="BackgroundImageFix"
//               result="effect1_dropShadow_1269_174429"
//             />
//             <feBlend
//               mode="normal"
//               in="SourceGraphic"
//               in2="effect1_dropShadow_1269_174429"
//               result="shape"
//             />
//           </filter>
//           <filter
//             id="filter6_d_1269_174429"
//             x="-455.102"
//             y="-836.02"
//             width="2455.2"
//             height="3140.2"
//             filterUnits="userSpaceOnUse"
//             color-interpolation-filters="sRGB"
//           >
//             <feFlood flood-opacity="0" result="BackgroundImageFix" />
//             <feColorMatrix
//               in="SourceAlpha"
//               type="matrix"
//               values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
//               result="hardAlpha"
//             />
//             <feOffset dy="14.0816" />
//             <feGaussianBlur stdDeviation="440.051" />
//             <feComposite in2="hardAlpha" operator="out" />
//             <feColorMatrix
//               type="matrix"
//               values="0 0 0 0 0.486275 0 0 0 0 0 0 0 0 0 0.678431 0 0 0 0.6 0"
//             />
//             <feBlend
//               mode="normal"
//               in2="BackgroundImageFix"
//               result="effect1_dropShadow_1269_174429"
//             />
//             <feBlend
//               mode="normal"
//               in="SourceGraphic"
//               in2="effect1_dropShadow_1269_174429"
//               result="shape"
//             />
//           </filter>
//           <filter
//             id="filter7_d_1269_174429"
//             x="-117.102"
//             y="1792.98"
//             width="3140.2"
//             height="3140.2"
//             filterUnits="userSpaceOnUse"
//             color-interpolation-filters="sRGB"
//           >
//             <feFlood flood-opacity="0" result="BackgroundImageFix" />
//             <feColorMatrix
//               in="SourceAlpha"
//               type="matrix"
//               values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
//               result="hardAlpha"
//             />
//             <feOffset dy="14.0816" />
//             <feGaussianBlur stdDeviation="440.051" />
//             <feComposite in2="hardAlpha" operator="out" />
//             <feColorMatrix
//               type="matrix"
//               values="0 0 0 0 0.486275 0 0 0 0 0 0 0 0 0 0.678431 0 0 0 0.6 0"
//             />
//             <feBlend
//               mode="normal"
//               in2="BackgroundImageFix"
//               result="effect1_dropShadow_1269_174429"
//             />
//             <feBlend
//               mode="normal"
//               in="SourceGraphic"
//               in2="effect1_dropShadow_1269_174429"
//               result="shape"
//             />
//           </filter>
//           <clipPath id="clip0_1269_174429">
//             <rect width="100%" height={height} fill="white" />
//           </clipPath>
//         </defs>
//       </svg>
//     </div>
//   );
// };

export const AnimatedHouse = () => {
  return (
    <svg
      width="52"
      height="56"
      viewBox="0 0 52 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        opacity="0.2"
        d="M1 30.1453L26.9671 1.7865C27.8155 0.859879 29.2982 0.935867 30.0475 1.94438L51 30.1453M1 30.1453H51M1 30.1453V52.6005C1 53.7051 1.89543 54.6005 3 54.6005H18.0702M51 30.1453V52.6005C51 53.7051 50.1046 54.6005 49 54.6005H35.1404M35.1404 54.6005V47.6416C35.1404 46.5371 34.245 45.6416 33.1404 45.6416H20.259C19.1755 45.6416 18.289 46.5045 18.2598 47.5876L18.0702 54.6005M35.1404 54.6005H18.0702"
        stroke="var(--primary-color)"
        stroke-linecap="round"
      />
    </svg>
  );
};

export const AnimatedHouse1 = () => {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.9997 3.33337V6.66671H30.943L37.943 18.3334H34.9997V36.6667H4.99969V18.3334H2.05469L9.05469 6.66671H11.6664V3.33337H14.9997ZM8.33302 18.3334V33.3334H14.9997V25H24.9997V33.3334H31.6664V18.3334H8.33302ZM32.0547 15L29.0547 10H10.943L7.94302 15H32.0547ZM21.6664 33.3334V28.3334H18.333V33.3334H21.6664Z"
        fill="var(--primary-color)"
        fillOpacity="0.1"
      />
    </svg>
  );
};

export const AnimatedHouse2 = () => {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M25 37.5H32.5V40H22.5V22.5H15V40H5V19.2773L3.37891 20.8789L1.62109 19.1211L18.75 1.99219L35.8789 19.1211L34.1211 20.8789L32.5 19.2773V22.5H30V16.7773L18.75 5.50781L7.5 16.7773V37.5H12.5V20H25V37.5ZM40 25V40H37.5V35H27.5V25H40ZM37.5 27.5H30V32.5H37.5V27.5Z"
        fill="var(--primary-color)"
        fillOpacity="0.1"
      />
    </svg>
  );
};

export const AnimatedHouse3 = () => {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3.33203 36.6667H36.6654M24.9987 36.6667V27.1217C24.9987 24.1084 22.7604 21.6667 19.9987 21.6667C17.237 21.6667 14.9987 24.1084 14.9987 27.1217V36.6667M28.332 16.6667H33.332M6.66536 16.6667H11.6654M19.1654 16.6667H20.832"
        stroke="var(--primary-color)"
        strokeOpacity="0.1"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M6.66667 36.6667V10H33.3333V36.6667M8.33333 10V8.33337C8.33333 5.97671 8.33333 4.79837 9.06667 4.06671C9.79667 3.33337 10.975 3.33337 13.3333 3.33337H26.6667C29.0233 3.33337 30.2017 3.33337 30.9333 4.06671C31.6667 4.79671 31.6667 5.97504 31.6667 8.33337V10M5 10H35"
        stroke="var(--primary-color)"
        strokeOpacity="0.1"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};

export const DefaultSvg = () => {
  return (
    <div className="size-[8rem] text-themeColor mx-auto mb-6">
      {" "}
      <svg
        width="100%"
        height="auto"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11.18 6.06C11.743 5.6525 12.2014 5.11745 12.5177 4.49866C12.8341 3.87988 12.9993 3.19496 13 2.5V2H14V0H2V2H3V2.5C2.99991 3.19255 3.16366 3.8753 3.47789 4.49245C3.79212 5.10961 4.2479 5.64367 4.808 6.051C5.14277 6.26038 5.42456 6.54436 5.63134 6.88074C5.83813 7.21711 5.96431 7.59676 6 7.99C5.95748 8.39204 5.82715 8.77982 5.61824 9.12594C5.40932 9.47206 5.12692 9.76805 4.791 9.993C4.24356 10.4001 3.79731 10.9279 3.48687 11.5354C3.17644 12.143 3.01018 12.8138 3.001 13.496V13.999H2.001V15.999H14.001V13.999H13.001V13.499C12.9933 12.8203 12.8298 12.1525 12.5231 11.547C12.2164 10.9415 11.7747 10.4147 11.232 10.007C10.893 9.78499 10.6071 9.49106 10.3945 9.1461C10.1819 8.80113 10.0479 8.41359 10.002 8.011C10.0333 7.61839 10.1552 7.23837 10.358 6.90074C10.5608 6.56311 10.8391 6.27706 11.171 6.065L11.18 6.06ZM9 8C9.03405 8.544 9.18786 9.07376 9.45045 9.5514C9.71304 10.029 10.0779 10.4428 10.519 10.763C10.9607 11.0725 11.3244 11.4804 11.5814 11.9545C11.8384 12.4286 11.9817 12.956 12 13.495V14H4V13.5C4.01637 12.9643 4.15663 12.4396 4.40976 11.9672C4.6629 11.4947 5.02203 11.0874 5.459 10.777C5.90433 10.4586 6.27346 10.0453 6.53977 9.56702C6.80608 9.08871 6.96292 8.55726 6.999 8.011C6.97134 7.47896 6.82189 6.96037 6.56217 6.4952C6.30246 6.03004 5.93941 5.63071 5.501 5.328C5.04024 5.01383 4.66278 4.59234 4.40112 4.09986C4.13946 3.60738 4.00146 3.05867 3.999 2.501V2.001H11.999V2.501C11.9977 3.05441 11.8625 3.5993 11.605 4.08913C11.3474 4.57897 10.9752 4.99922 10.52 5.314C10.0785 5.61429 9.71181 6.01187 9.44812 6.47615C9.18443 6.94042 9.03078 7.45901 8.999 7.992L9 8Z"
          fill="currentColor"
        />
      </svg>{" "}
    </div>
  );
};

export const DefaultProperty = () => {
  return (
    <div className="size-[8rem] text-themeColor mx-auto mb-6">
      <svg
        width="100%"
        height="auto"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_20266_13129)">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M1.00218 10.9993C0.0821844 10.9993 -0.349816 9.86227 0.338184 9.25227L9.33818 1.25227C9.52119 1.08976 9.75744 1 10.0022 1C10.2469 1 10.4832 1.08976 10.6662 1.25227L19.6662 9.25227C20.3542 9.86227 19.9222 10.9993 19.0022 10.9993H18.0022V17.9993C18.0022 18.2645 17.8968 18.5188 17.7093 18.7064C17.5218 18.8939 17.2674 18.9993 17.0022 18.9993H3.00218C2.73697 18.9993 2.48261 18.8939 2.29508 18.7064C2.10754 18.5188 2.00218 18.2645 2.00218 17.9993V10.9993H1.00218ZM7.00218 16.9993V11.9993C7.00218 11.7341 7.10754 11.4797 7.29508 11.2922C7.48261 11.1046 7.73697 10.9993 8.00218 10.9993H12.0022C12.2674 10.9993 12.5218 11.1046 12.7093 11.2922C12.8968 11.4797 13.0022 11.7341 13.0022 11.9993V16.9993H16.0022V9.99927C16.0022 9.82136 16.0496 9.64667 16.1396 9.49321C16.2296 9.33976 16.3589 9.21308 16.5142 9.12627L10.0022 3.33627L3.49018 9.12627C3.64547 9.21308 3.77478 9.33976 3.86478 9.49321C3.95478 9.64667 4.00221 9.82136 4.00218 9.99927V16.9993H7.00218ZM9.00218 16.9993V12.9993H11.0022V16.9993H9.00218Z"
            fill="currentColor"
          />
          <path
            d="M1.29634 2.70252C1.11418 2.51392 1.01338 2.26132 1.01566 1.99912C1.01794 1.73693 1.12311 1.48611 1.30852 1.30071C1.49393 1.1153 1.74474 1.01013 2.00694 1.00785C2.26913 1.00557 2.52173 1.10637 2.71034 1.28852L18.7103 17.2885C18.8925 17.4771 18.9933 17.7297 18.991 17.9919C18.9887 18.2541 18.8836 18.5049 18.6982 18.6903C18.5127 18.8758 18.2619 18.9809 17.9997 18.9832C17.7375 18.9855 17.4849 18.8847 17.2963 18.7025L1.29634 2.70252Z"
            fill="currentColor"
          />
        </g>
        <defs>
          <clipPath id="clip0_20266_13129">
            <rect width="20" height="20" fill="white" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
};

export const NotFoundProperty = () => {
  return (
    <span className="size-[14rem] block text-themeColor mx-auto mb-6">
      <svg
        width="100%"
        height="auto"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M9.59194 8.27679L8.69394 6.89279L12.1539 4.64579L13.0519 6.02979L9.59194 8.27679ZM9.10694 10.5588L8.20794 9.17479L7.30994 7.79079L6.40994 6.40779L7.79494 5.50779L11.2549 3.26179L12.6379 2.36279L13.5379 3.74679L14.4359 5.13079L15.3349 6.51479L13.9509 7.41379L10.4909 9.66079L9.10694 10.5588ZM13.5769 14.6218L15.2739 15.4868L15.1739 13.5838L14.9859 9.97379L14.8859 8.07179L13.2879 9.10979L10.2559 11.0778L8.65794 12.1158L10.3559 12.9808L13.5769 14.6218ZM13.4769 12.7188L13.3869 11.0118L11.9539 11.9428L13.4769 12.7188ZM17.7349 11.9638H20.2099V20.2148H17.7349V11.9638ZM21.8599 10.3138V21.8648H16.0849V10.3148L21.8599 10.3138ZM8.20794 11.0118L4.44794 21.9338L2.88794 21.3968L6.64794 10.4748L8.20794 11.0118ZM10.7209 20.2148C10.9956 20.2208 11.2687 20.172 11.5243 20.071C11.7798 19.9701 12.0126 19.8191 12.209 19.627C12.4054 19.4349 12.5614 19.2055 12.6679 18.9522C12.7745 18.699 12.8294 18.427 12.8294 18.1523C12.8294 17.8776 12.7745 17.6056 12.6679 17.3523C12.5614 17.0991 12.4054 16.8697 12.209 16.6776C12.0126 16.4854 11.7798 16.3345 11.5243 16.2336C11.2687 16.1326 10.9956 16.0837 10.7209 16.0898C10.1818 16.1017 9.66873 16.3242 9.29163 16.7097C8.91452 17.0952 8.70336 17.613 8.70336 18.1523C8.70336 18.6916 8.91452 19.2094 9.29163 19.5949C9.66873 19.9804 10.1818 20.2029 10.7209 20.2148ZM14.4339 18.1518C14.442 18.6445 14.352 19.1338 14.169 19.5913C13.9861 20.0488 13.7139 20.4653 13.3684 20.8166C13.0228 21.1678 12.6108 21.4468 12.1564 21.6372C11.702 21.8277 11.2142 21.9257 10.7214 21.9257C10.2287 21.9257 9.74092 21.8277 9.28647 21.6372C8.83203 21.4468 8.42004 21.1678 8.0745 20.8166C7.72896 20.4653 7.45679 20.0488 7.27385 19.5913C7.0909 19.1338 7.00085 18.6445 7.00894 18.1518C7.02493 17.1777 7.42309 16.249 8.11758 15.5658C8.81207 14.8826 9.74724 14.4997 10.7214 14.4997C11.6956 14.4997 12.6308 14.8826 13.3253 15.5658C14.0198 16.249 14.418 17.1777 14.4339 18.1518Z"
          fill="currentColor"
        />
      </svg>
    </span>
  );
};

export const NoNetwork = () => {
  return (
    <span className="size-[14rem] block text-themeColor mx-auto mb-6">
      <svg
        width="24"
        height="17"
        viewBox="0 0 24 17"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M4.35005 6.5C5.46672 5.7 6.67505 5.08333 7.97505 4.65C9.27505 4.21667 10.6167 4 12 4C13.3834 4 14.725 4.21667 16.025 4.65C17.325 5.08333 18.5334 5.7 19.65 6.5L21.1 5.05C19.7834 4.05 18.35 3.29167 16.8 2.775C15.25 2.25833 13.65 2 12 2C10.35 2 8.75005 2.25833 7.20005 2.775C5.65005 3.29167 4.21672 4.05 2.90005 5.05L4.35005 6.5ZM10.575 15.575L1.52505 6.525C1.32505 6.325 1.17505 6.1 1.07505 5.85C0.975049 5.6 0.925049 5.35 0.925049 5.1C0.925049 4.81667 0.983382 4.546 1.10005 4.288C1.21672 4.03 1.39172 3.80067 1.62505 3.6C2.99172 2.41667 4.61672 1.521 6.50005 0.913001C8.38338 0.305001 10.2167 0.00066776 12 1.0929e-06C13.7834 -0.000665574 15.6167 0.303668 17.5 0.913001C19.3834 1.52233 21.0084 2.418 22.375 3.6C22.6084 3.8 22.7834 4.02933 22.9 4.288C23.0167 4.54667 23.0751 4.81733 23.0751 5.1C23.0751 5.35 23.025 5.6 22.925 5.85C22.825 6.1 22.675 6.325 22.475 6.525L13.425 15.575C13.225 15.775 13 15.925 12.75 16.025C12.5 16.125 12.25 16.175 12 16.175C11.75 16.175 11.5 16.125 11.25 16.025C11 15.925 10.775 15.775 10.575 15.575Z"
          fill="currentColor"
        />
      </svg>
    </span>
  );
};

export const ChevronRight = () => {
  return (
    <svg
      width="13"
      height="24"
      viewBox="0 0 13 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M0.33595 2.02351C0.227752 1.90901 0.143166 1.77433 0.0870199 1.62714C0.0308743 1.47995 0.00426865 1.32314 0.00872326 1.16567C0.0131779 1.0082 0.048605 0.853153 0.112982 0.709374C0.177359 0.565596 0.269424 0.435905 0.383922 0.327707C0.49842 0.219508 0.633109 0.134922 0.780297 0.078776C0.927485 0.0226303 1.08429 -0.00397488 1.24176 0.000479721C1.39923 0.00493432 1.55428 0.0403613 1.69806 0.104738C1.84184 0.169115 1.97153 0.26118 2.07973 0.375678L12.2737 11.1693C12.4843 11.392 12.6016 11.6868 12.6016 11.9933C12.6016 12.2997 12.4843 12.5945 12.2737 12.8172L2.07973 23.612C1.97224 23.729 1.84258 23.8235 1.69827 23.89C1.55397 23.9565 1.39789 23.9936 1.23911 23.9992C1.08033 24.0049 0.92201 23.9789 0.77335 23.9229C0.624689 23.8668 0.488649 23.7818 0.373132 23.6727C0.257615 23.5636 0.164924 23.4326 0.100442 23.2874C0.0359602 23.1422 0.000973701 22.9857 -0.00248528 22.8268C-0.00594425 22.668 0.022192 22.51 0.0802908 22.3621C0.13839 22.2143 0.225292 22.0794 0.33595 21.9654L9.75282 11.9933L0.33595 2.02351Z"
        fill="currentColor"
      />
    </svg>
  );
};

export const VerifiedBadge = ({
  verified,
  color,
}: {
  verified: boolean;
  color?: string;
}) => {
  if (!verified) return;
  return (
    <svg
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_14283_27736)">
        <path
          d="M14.4141 8.50195L15.3828 9.71289C15.4766 9.83268 15.5495 9.96289 15.6016 10.1035C15.6536 10.2441 15.6797 10.3926 15.6797 10.5488C15.6797 10.7051 15.6562 10.8535 15.6094 10.9941C15.5625 11.1348 15.4948 11.2624 15.4062 11.377C15.3177 11.4915 15.2109 11.5905 15.0859 11.6738C14.9609 11.7572 14.8255 11.8197 14.6797 11.8613L13.1875 12.2754C13.1979 12.541 13.2109 12.8092 13.2266 13.0801C13.2422 13.3509 13.2526 13.6217 13.2578 13.8926C13.2578 14.0801 13.224 14.2546 13.1562 14.416C13.0885 14.5775 12.9922 14.7181 12.8672 14.8379C12.7422 14.9577 12.599 15.054 12.4375 15.127C12.276 15.1999 12.099 15.2363 11.9062 15.2363C11.7448 15.2363 11.5859 15.2077 11.4297 15.1504L9.98438 14.6035L9.13281 15.8926C9.00781 16.0853 8.84635 16.2337 8.64844 16.3379C8.45052 16.4421 8.23438 16.4967 8 16.502C7.77083 16.502 7.55729 16.4499 7.35938 16.3457C7.16146 16.2415 6.9974 16.0905 6.86719 15.8926L6.01562 14.6035L4.57031 15.1504C4.41406 15.2077 4.25521 15.2363 4.09375 15.2363C3.90625 15.2363 3.73177 15.2025 3.57031 15.1348C3.40885 15.0671 3.26562 14.9707 3.14062 14.8457C3.01562 14.7207 2.91927 14.5775 2.85156 14.416C2.78385 14.2546 2.7474 14.0801 2.74219 13.8926C2.74219 13.6217 2.75 13.3509 2.76562 13.0801C2.78125 12.8092 2.79688 12.541 2.8125 12.2754L1.32031 11.8613C1.16927 11.8197 1.03385 11.7572 0.914062 11.6738C0.794271 11.5905 0.690104 11.4915 0.601562 11.377C0.513021 11.2624 0.445312 11.1348 0.398438 10.9941C0.351562 10.8535 0.325521 10.7051 0.320312 10.5488C0.320312 10.3978 0.346354 10.252 0.398438 10.1113C0.450521 9.9707 0.523438 9.83789 0.617188 9.71289L1.58594 8.50195L0.617188 7.29102C0.419271 7.04622 0.320312 6.76758 0.320312 6.45508C0.320312 6.29883 0.34375 6.15039 0.390625 6.00977C0.4375 5.86914 0.505208 5.74154 0.59375 5.62695C0.682292 5.51237 0.786458 5.41341 0.90625 5.33008C1.02604 5.24674 1.16406 5.18424 1.32031 5.14258L2.8125 4.72852C2.80208 4.46289 2.78906 4.19466 2.77344 3.92383C2.75781 3.65299 2.7474 3.38216 2.74219 3.11133C2.74219 2.92383 2.77604 2.74935 2.84375 2.58789C2.91146 2.42643 3.00781 2.28581 3.13281 2.16602C3.25781 2.04622 3.40104 1.94987 3.5625 1.87695C3.72396 1.80404 3.90104 1.76758 4.09375 1.76758C4.25521 1.76758 4.41406 1.79622 4.57031 1.85352L6.01562 2.40039L6.86719 1.11133C6.99219 0.91862 7.15365 0.770182 7.35156 0.666016C7.54948 0.561849 7.76562 0.507161 8 0.501953C8.22917 0.501953 8.44271 0.554036 8.64062 0.658203C8.83854 0.76237 9.0026 0.913411 9.13281 1.11133L9.98438 2.40039L11.4297 1.85352C11.5859 1.79622 11.7448 1.76758 11.9062 1.76758C12.0938 1.76758 12.2682 1.80143 12.4297 1.86914C12.5911 1.93685 12.7344 2.0332 12.8594 2.1582C12.9844 2.2832 13.0807 2.42643 13.1484 2.58789C13.2161 2.74935 13.2526 2.92383 13.2578 3.11133C13.2578 3.38216 13.25 3.65299 13.2344 3.92383C13.2188 4.19466 13.2031 4.46289 13.1875 4.72852L14.6797 5.14258C14.8255 5.18424 14.9609 5.24674 15.0859 5.33008C15.2109 5.41341 15.3151 5.51237 15.3984 5.62695C15.4818 5.74154 15.5495 5.86914 15.6016 6.00977C15.6536 6.15039 15.6797 6.29883 15.6797 6.45508C15.6797 6.76758 15.5807 7.04622 15.3828 7.29102L14.4141 8.50195ZM7 11.3848L11.4453 6.94727L10.5547 6.05664L7 9.61914L5.44531 8.05664L4.55469 8.94727L7 11.3848Z"
          fill={color || "#808080"}
        />
      </g>
      <defs>
        <clipPath id="clip0_14283_27736">
          <rect
            width="16"
            height="16"
            fill="white"
            transform="translate(0 0.501953)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};
